import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

import { PageLayout } from '@components/layouts/PageLayout'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@components/atoms/Breadcrumb'
import { Button } from '@atoms'
import TextInput from '@components/atoms/form/TextInput'
import { SelectField } from '@components/molecules/SelectField'
import Badge from '@components/atoms/Badge'
import Icon, { type IconTypes } from '@components/utils/Icon'
import { RichTextEditor } from '@components/atoms/form/RichTextEditor'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'
import { Sheet, SheetContent, SheetTitle } from '@components/atoms/Sheet'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from '@components/atoms/DropdownMenu'

/**
 * Mailers settings (design-phase prototype)
 *
 * A single place for admins to edit every mailer their SupporterBase sends,
 * following on from the editable Liquid shift-mailer templates shipped in
 * #6883 / #6907 (which added the `mailer_templates` table and the Liquid
 * rendering pipeline but no UI). It covers the mailers currently split across
 * the "Mailers" and "Account emails" tabs of /app/settings, plus the mailer
 * *layouts* we expect to make editable later.
 *
 * The index row's button is labelled "Customise": it agrees with the
 * "Customised" badge on the same row, so the verb and the noun are the same
 * word, and it says what the mailer becomes rather than what you do to it. The
 * runner-up was "Edit" — shorter, and neutral about whether you are departing
 * from the default, which "Customise" is not: it reads oddly for a typo fix in
 * an already-customised mailer, or for a read.
 *
 * Labels are a discovery question either way, so treat this as an assumption to
 * test: ask admins how they describe the task in their own words, then run a
 * first-click test ("where would you go to change the wording of the shift
 * email?") across the candidates before shipping.
 *
 * Everything here is local sample data — there is no controller, GraphQL or
 * mutation behind it. The Liquid rendering in the preview pane is a small
 * client-side approximation of `MailerTemplates::Engines::Liquid` so that the
 * sample-data controls actually do something. The "Design tools" overlay at the
 * bottom-left is dev-only, and offers two competing treatments of scoped
 * overrides (`mailer_templates.owner`) to compare.
 */

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

type GroupKey = 'shifts' | 'events' | 'supporters' | 'approvals' | 'account' | 'layouts'

type SmartField = { token: string; label: string }
type SmartFieldGroup = { label: string; fields: SmartField[] }

type Mailer = {
  id: string
  name: string
  /** What triggers it, in admin-facing language. */
  description: string
  group: GroupKey
  /** Migrated to the Liquid `mailer_templates` pipeline (#6883/#6907). */
  migrated: boolean
  /** Whether the mailer can be switched off entirely (`Definition.suppressible?`). */
  suppressible: boolean
  enabled: boolean
  /** Has an instance-level row in `mailer_templates`, i.e. edited away from the code default. */
  customised: boolean
  subject: string
  preheader: string
  bodyHtml: string
  smartFields: SmartFieldGroup[]
  /** Which layout it renders inside. */
  layoutId: string
  /**
   * The single owner type this mailer key can be scoped by, declared on its
   * `MailerTemplates::Definitions::*`. `null` means instance-wide only.
   */
  overrideScope: OwnerKind | null
  /** Anchor on the current v1 settings page, for the not-yet-migrated ones. */
  legacyHref?: string
}

type EmailLayout = {
  id: string
  name: string
  description: string
  usedBy: number
  bodyHtml: string
  smartFields: SmartFieldGroup[]
}

const RECIPIENT_FIELDS: SmartFieldGroup = {
  label: 'Recipient',
  fields: [{ token: '{{ recipient.first_name }}', label: 'First name' }],
}

const EVENT_FIELDS: SmartFieldGroup = {
  label: 'Event',
  fields: [
    { token: '{{ event.name }}', label: 'Name' },
    { token: '{{ event.url }}', label: 'Public URL' },
    { token: '{{ event.location }}', label: 'Venue name and address' },
    { token: '{{ event.full_venue_address }}', label: 'Full address' },
    { token: '{{ event.date_and_time_details }}', label: 'Date and time' },
  ],
}

const SHIFT_FIELDS: SmartFieldGroup = {
  label: 'Shifts',
  fields: [
    { token: '{% for shift in shifts %}…{% endfor %}', label: 'Loop over the shifts' },
    { token: '{{ shift.summary }}', label: 'Shift summary line' },
    { token: '{{ shift.role }}', label: 'Role name' },
    { token: '{{ shift.start_time }}', label: 'Start time' },
    { token: '{{ shift.end_time }}', label: 'End time' },
  ],
}

const OTHER_RSVP_FIELDS: SmartFieldGroup = {
  label: 'Other upcoming shifts',
  fields: [
    { token: '{% for rsvp in other_upcoming_rsvps %}…{% endfor %}', label: 'Loop over other events' },
    { token: '{{ rsvp.event.name }}', label: 'Event name' },
    { token: '{% for shift in rsvp.shifts %}…{% endfor %}', label: 'Loop over that event’s shifts' },
  ],
}

const LAYOUT_FIELDS: SmartFieldGroup = {
  label: 'Layout',
  fields: [
    { token: '{{ content }}', label: 'The mailer body (required)' },
    { token: '{{ instance.name }}', label: 'SupporterBase name' },
    { token: '{{ instance.logo_url }}', label: 'Logo URL' },
    { token: '{{ unsubscribe_url }}', label: 'Unsubscribe link' },
  ],
}

const SHIFT_SMART_FIELDS = [RECIPIENT_FIELDS, EVENT_FIELDS, SHIFT_FIELDS, OTHER_RSVP_FIELDS]

const GREETING = '<h1 style="margin:0 0 24px;font-size:20px;line-height:32px;font-weight:600;color:#0f172a">Hi {{ recipient.first_name }},</h1>'
const SIGN_OFF = '<p style="margin:24px 0 0;">Thanks,<br>The team</p>'
const RULE = '<hr style="border:0;border-top:1px solid #cbd5e1;margin:24px 0;">'

const SHIFT_LIST = `<ul style="padding-left:24px;margin:0;">
{% for shift in shifts %}
  <li style="margin-bottom:16px;">{{ shift.summary }}</li>
{% endfor %}
</ul>`

const OTHER_RSVPS = `{% if other_upcoming_rsvps.size > 0 %}
${RULE}
<p style="font-weight:600;margin:0 0 16px;">Your other upcoming shifts</p>
{% for rsvp in other_upcoming_rsvps %}
<p style="margin:0 0 8px;font-weight:600;">{{ rsvp.event.name }}</p>
<ul style="padding-left:24px;margin:0 0 16px;">
{% for shift in rsvp.shifts %}
  <li style="margin-bottom:8px;">{{ shift.summary }}</li>
{% endfor %}
</ul>
{% endfor %}
{% endif %}`

const MAILERS: Mailer[] = [
  {
    id: 'shift_slots_assigned',
    name: 'Shift assignments',
    description: 'Sent to a supporter when they are assigned to one or more shifts.',
    group: 'shifts',
    migrated: true,
    suppressible: true,
    enabled: true,
    customised: true,
    subject: 'Your shifts for {{ event.name }}',
    preheader: 'Your shift assignments',
    layoutId: 'default',
    overrideScope: 'event_type',
    smartFields: SHIFT_SMART_FIELDS,
    bodyHtml: `${GREETING}

<p style="margin:0;">Thanks for volunteering at {{ event.name }}: <a href="{{ event.url }}" target="_blank" style="color:#0284c7;text-decoration:underline;font-weight:600;">{{ event.url }}</a>.</p>

{% if event.location.size > 0 %}
<p style="margin:0;padding-top:1rem;">The event is at {{ event.location }}.</p>
{% endif %}

${RULE}

<p style="font-weight:600;margin:0 0 16px;">Your shift assignments</p>

${SHIFT_LIST}

${OTHER_RSVPS}

${SIGN_OFF}`,
  },
  {
    id: 'shifts_changed',
    name: 'Shift details changed',
    description: 'Sent when the time, role or details of a shift a supporter is on are edited.',
    group: 'shifts',
    migrated: true,
    suppressible: true,
    enabled: true,
    customised: false,
    subject: 'A change to your shifts for {{ event.name }}',
    preheader: 'Your shift details have changed',
    layoutId: 'default',
    overrideScope: 'event_type',
    smartFields: SHIFT_SMART_FIELDS,
    bodyHtml: `${GREETING}

<p style="margin:0;">Some details have changed for the shifts you signed up for at {{ event.name }}: <a href="{{ event.url }}" target="_blank" style="color:#0284c7;text-decoration:underline;font-weight:600;">{{ event.url }}</a>.</p>

${RULE}

<p style="font-weight:600;margin:0 0 16px;">Your shifts, as they now stand</p>

${SHIFT_LIST}

${SIGN_OFF}`,
  },
  {
    id: 'shift_slots_partially_unassigned',
    name: 'Some shifts removed',
    description: 'Sent when a supporter is taken off some, but not all, of their shifts at an event.',
    group: 'shifts',
    migrated: true,
    suppressible: true,
    enabled: true,
    customised: false,
    subject: 'A change to your shifts for {{ event.name }}',
    preheader: 'Some of your shift assignments have been removed',
    layoutId: 'default',
    overrideScope: 'event_type',
    smartFields: SHIFT_SMART_FIELDS,
    bodyHtml: `${GREETING}

<p style="margin:0;">You have been taken off some of your shifts at {{ event.name }}. Here is what you are still down for:</p>

${RULE}

${SHIFT_LIST}

${OTHER_RSVPS}

${SIGN_OFF}`,
  },
  {
    id: 'shift_slots_completely_unassigned',
    name: 'All shifts removed',
    description: 'Sent when a supporter is taken off every shift they held at an event.',
    group: 'shifts',
    migrated: true,
    suppressible: true,
    enabled: false,
    customised: false,
    subject: 'Your shifts for {{ event.name }} have been removed',
    preheader: 'Your shift assignments for {{ event.name }} have been removed',
    layoutId: 'default',
    overrideScope: 'event_type',
    smartFields: SHIFT_SMART_FIELDS,
    bodyHtml: `${GREETING}

<p style="margin:0;">You are no longer assigned to any shifts at {{ event.name }}. If that is a surprise, reply to this email and we will sort it out.</p>

${OTHER_RSVPS}

${SIGN_OFF}`,
  },
  {
    id: 'event_approval',
    name: 'New event awaiting approval',
    description: 'Sent to approvers when a leader creates an event that needs sign-off.',
    group: 'events',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: true,
    subject: 'A new event needs your approval',
    preheader: '',
    layoutId: 'default',
    overrideScope: 'event_type',
    legacyHref: '/app/settings?tab=mailers#collapse-event-approval',
    smartFields: [
      EVENT_FIELDS,
      {
        label: 'Approval',
        fields: [
          { token: '{{ event.state }}', label: 'Event state' },
          { token: '{{ event.approval_url }}', label: 'Approval page URL' },
          { token: '{{ creator.name }}', label: 'Event creator’s full name' },
          { token: '{{ creator.email }}', label: 'Event creator’s email' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">{{ creator.name }} ({{ creator.email }}) has created <strong>{{ event.name }}</strong> and it is waiting for approval.</p>

<p style="margin:16px 0 0;"><a href="{{ event.approval_url }}" style="color:#0284c7;font-weight:600;">Review this event</a></p>`,
  },
  {
    id: 'event_confirmation',
    name: 'New event confirmation (host)',
    description: 'Sent to the host confirming their event was created.',
    group: 'events',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: false,
    subject: 'Your event has been created',
    preheader: '',
    layoutId: 'default',
    overrideScope: 'event_type',
    legacyHref: '/app/settings?tab=mailers#coordinators-conf',
    smartFields: [EVENT_FIELDS, { label: 'Host', fields: [{ token: '{{ creator.first_name }}', label: 'Event creator’s first name' }] }],
    bodyHtml: `<p style="margin:0;">Hi {{ creator.first_name }}, your event <strong>{{ event.name }}</strong> is live: <a href="{{ event.url }}" style="color:#0284c7;font-weight:600;">{{ event.url }}</a>.</p>`,
  },
  {
    id: 'new_supporter_autoresponse',
    name: 'New supporter autoresponse',
    description: 'Sent to a supporter when they first join.',
    group: 'supporters',
    migrated: false,
    suppressible: true,
    enabled: true,
    customised: true,
    subject: 'Welcome aboard',
    preheader: '',
    layoutId: 'default',
    overrideScope: 'group',
    legacyHref: '/app/settings?tab=mailers#collapse-new-supporter-autoresponse',
    smartFields: [
      {
        label: 'Supporter',
        fields: [
          { token: '{{ supporter.first_name }}', label: 'First name' },
          { token: '{{ supporter.last_name }}', label: 'Last name' },
          { token: '{{ supporter_group.name }}', label: 'Group name' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">Hi {{ supporter.first_name }}, thanks for joining {{ supporter_group.name }}. We will be in touch soon with ways to get involved.</p>`,
  },
  {
    id: 'new_supporter_notification',
    name: 'New supporter notification',
    description: 'Sent to leaders when a supporter joins one of their groups.',
    group: 'supporters',
    migrated: false,
    suppressible: true,
    enabled: true,
    customised: false,
    subject: 'A new supporter joined {{ supporter_group.name }}',
    preheader: '',
    layoutId: 'default',
    overrideScope: 'group',
    legacyHref: '/app/settings?tab=mailers#collapse-new-supporter-notification',
    smartFields: [
      {
        label: 'Supporter',
        fields: [
          { token: '{{ supporter.first_name }}', label: 'First name' },
          { token: '{{ supporter.last_name }}', label: 'Last name' },
          { token: '{{ supporter_group.name }}', label: 'Group name' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">{{ supporter.first_name }} {{ supporter.last_name }} has just joined {{ supporter_group.name }}.</p>`,
  },
  {
    id: 'member_removal',
    name: 'Supporter removal request',
    description: 'Sent to approvers when someone asks to remove a supporter from a group.',
    group: 'supporters',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: false,
    subject: 'A supporter removal needs your approval',
    preheader: '',
    layoutId: 'default',
    overrideScope: 'group',
    legacyHref: '/app/settings?tab=mailers#member-removal-conf',
    smartFields: [
      {
        label: 'Request',
        fields: [
          { token: '{{ supporter.name }}', label: 'Supporter’s full name' },
          { token: '{{ supporter_group.name }}', label: 'Group name' },
          { token: '{{ requester.name_or_email }}', label: 'Requester’s name or email' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">{{ requester.name_or_email }} has asked to remove {{ supporter.name }} from {{ supporter_group.name }}.</p>`,
  },
  {
    id: 'email_blast_approval',
    name: 'Email blast approval',
    description: 'Sent to approvers when a leader creates an email blast.',
    group: 'approvals',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: true,
    subject: 'An email blast needs your approval',
    preheader: '',
    layoutId: 'default',
    overrideScope: null,
    legacyHref: '/app/settings?tab=mailers#collapse-email-blast-approval',
    smartFields: [
      {
        label: 'Blast',
        fields: [
          { token: '{{ blast.subject }}', label: 'Email blast subject' },
          { token: '{{ blast.url }}', label: 'Email blast URL' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">An email blast, “{{ blast.subject }}”, is waiting for approval: <a href="{{ blast.url }}" style="color:#0284c7;font-weight:600;">review it here</a>.</p>`,
  },
  {
    id: 'sms_blast_approval',
    name: 'SMS blast approval',
    description: 'Sent to approvers when a leader creates an SMS blast.',
    group: 'approvals',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: false,
    subject: 'An SMS blast needs your approval',
    preheader: '',
    layoutId: 'default',
    overrideScope: null,
    legacyHref: '/app/settings?tab=mailers#collapse-sms-blast-approval',
    smartFields: [
      {
        label: 'Blast',
        fields: [
          { token: '{{ blast.body }}', label: 'SMS blast body' },
          { token: '{{ blast.url }}', label: 'SMS blast URL' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">An SMS blast is waiting for approval: <a href="{{ blast.url }}" style="color:#0284c7;font-weight:600;">review it here</a>.</p>

<blockquote style="margin:16px 0 0;padding-left:16px;border-left:3px solid #cbd5e1;">{{ blast.body }}</blockquote>`,
  },
  {
    id: 'admin_invitation',
    name: 'Admin invitation',
    description: 'Sent when a new admin is invited to the SupporterBase.',
    group: 'account',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: true,
    subject: 'You have been invited as an admin',
    preheader: '',
    layoutId: 'account',
    overrideScope: null,
    legacyHref: '/app/settings?tab=account_emails#collapse-admin-invitation',
    smartFields: [
      {
        label: 'Invitation',
        fields: [
          { token: '{{ inviter.name_or_email }}', label: 'Inviter’s name or email' },
          { token: '{{ invitee.name_or_email }}', label: 'Invitee’s name or email' },
          { token: '{{ invitation_url }}', label: 'Invitation link' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">{{ inviter.name_or_email }} has invited you to help run this SupporterBase as an admin.</p>

<p style="margin:16px 0 0;"><a href="{{ invitation_url }}" style="color:#0284c7;font-weight:600;">Accept your invitation</a></p>`,
  },
  {
    id: 'captain_invitation',
    name: 'Captain invitation',
    description: 'Sent when someone is invited to lead a group as captain.',
    group: 'account',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: false,
    subject: 'You have been invited as a captain',
    preheader: '',
    layoutId: 'account',
    overrideScope: 'group',
    legacyHref: '/app/settings?tab=account_emails#collapse-captain-invitation',
    smartFields: [
      {
        label: 'Invitation',
        fields: [
          { token: '{{ inviter.name_or_email }}', label: 'Inviter’s name or email' },
          { token: '{{ invitee.name_or_email }}', label: 'Invitee’s name or email' },
          { token: '{{ supporter_group_names }}', label: 'Group names' },
          { token: '{{ invitation_url }}', label: 'Invitation link' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">{{ inviter.name_or_email }} has invited you to lead {{ supporter_group_names }} as captain.</p>

<p style="margin:16px 0 0;"><a href="{{ invitation_url }}" style="color:#0284c7;font-weight:600;">Accept your invitation</a></p>`,
  },
  {
    id: 'vice_captain_invitation',
    name: 'Vice-captain invitation',
    description: 'Sent when someone is invited to help lead a group as vice-captain.',
    group: 'account',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: false,
    subject: 'You have been invited as a vice-captain',
    preheader: '',
    layoutId: 'account',
    overrideScope: 'group',
    legacyHref: '/app/settings?tab=account_emails#collapse-vice-captain-invitation',
    smartFields: [
      {
        label: 'Invitation',
        fields: [
          { token: '{{ inviter.name_or_email }}', label: 'Inviter’s name or email' },
          { token: '{{ invitee.name_or_email }}', label: 'Invitee’s name or email' },
          { token: '{{ supporter_group_names }}', label: 'Group names' },
          { token: '{{ invitation_url }}', label: 'Invitation link' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">{{ inviter.name_or_email }} has invited you to help lead {{ supporter_group_names }} as vice-captain.</p>

<p style="margin:16px 0 0;"><a href="{{ invitation_url }}" style="color:#0284c7;font-weight:600;">Accept your invitation</a></p>`,
  },
  {
    id: 'reset_password',
    name: 'Reset password',
    description: 'Sent when a user asks to reset their password.',
    group: 'account',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: false,
    subject: 'Reset your password',
    preheader: '',
    layoutId: 'account',
    overrideScope: null,
    legacyHref: '/app/settings?tab=account_emails#collapse-reset-password',
    smartFields: [
      {
        label: 'Account',
        fields: [
          { token: '{{ user.name_or_email }}', label: 'User’s name or email' },
          { token: '{{ reset_password_url }}', label: 'Reset password link' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">Hi {{ user.name_or_email }}, someone asked to reset your password. If that was you:</p>

<p style="margin:16px 0 0;"><a href="{{ reset_password_url }}" style="color:#0284c7;font-weight:600;">Choose a new password</a></p>`,
  },
  {
    id: 'unlock_account',
    name: 'Unlock account',
    description: 'Sent when an account is locked after too many failed sign-ins.',
    group: 'account',
    migrated: false,
    suppressible: false,
    enabled: true,
    customised: false,
    subject: 'Unlock your account',
    preheader: '',
    layoutId: 'account',
    overrideScope: null,
    legacyHref: '/app/settings?tab=account_emails#collapse-unlock-account',
    smartFields: [
      {
        label: 'Account',
        fields: [
          { token: '{{ user.name_or_email }}', label: 'User’s name or email' },
          { token: '{{ unlock_account_url }}', label: 'Unlock account link' },
        ],
      },
    ],
    bodyHtml: `<p style="margin:0;">Hi {{ user.name_or_email }}, your account was locked after too many sign-in attempts.</p>

<p style="margin:16px 0 0;"><a href="{{ unlock_account_url }}" style="color:#0284c7;font-weight:600;">Unlock your account</a></p>`,
  },
]

const LAYOUTS: EmailLayout[] = [
  {
    id: 'default',
    name: 'Default layout',
    description: 'Wraps every supporter-facing and leader-facing mailer.',
    usedBy: 11,
    smartFields: [LAYOUT_FIELDS],
    bodyHtml: `<div style="background:#f4f4f6;padding:24px 0;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;font-family:Inter,Helvetica,Arial,sans-serif;color:#334155;">
    <tr>
      <td style="padding:24px;border-bottom:1px solid #e2e8f0;">
        <strong style="font-size:16px;color:#6927DA;">{{ instance.name }}</strong>
      </td>
    </tr>
    <tr>
      <td style="padding:32px 24px;font-size:15px;line-height:24px;">
        {{ content }}
      </td>
    </tr>
    <tr>
      <td style="padding:20px 24px;background:#f9f9fb;font-size:12px;line-height:18px;color:#7D89B0;">
        You are receiving this because you are a supporter of {{ instance.name }}.<br>
        <a href="{{ unsubscribe_url }}" style="color:#7D89B0;">Unsubscribe</a>
      </td>
    </tr>
  </table>
</div>`,
  },
  {
    id: 'account',
    name: 'Account and security layout',
    description: 'Used for invitations, password resets and account unlocks. No unsubscribe link.',
    usedBy: 5,
    smartFields: [LAYOUT_FIELDS],
    bodyHtml: `<div style="background:#f4f4f6;padding:24px 0;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;font-family:Inter,Helvetica,Arial,sans-serif;color:#334155;">
    <tr>
      <td style="padding:24px;border-bottom:1px solid #e2e8f0;">
        <strong style="font-size:16px;color:#6927DA;">{{ instance.name }}</strong>
      </td>
    </tr>
    <tr>
      <td style="padding:32px 24px;font-size:15px;line-height:24px;">
        {{ content }}
      </td>
    </tr>
    <tr>
      <td style="padding:20px 24px;background:#f9f9fb;font-size:12px;line-height:18px;color:#7D89B0;">
        This is an account message, so it is sent whatever your contact preferences are.
      </td>
    </tr>
  </table>
</div>`,
  },
]

const GROUPS: Array<{ key: GroupKey; label: string; icon: IconTypes }> = [
  { key: 'shifts', label: 'Shifts', icon: 'clock' },
  { key: 'events', label: 'Events', icon: 'calendar' },
  { key: 'supporters', label: 'Supporters', icon: 'users-01' },
  { key: 'approvals', label: 'Approvals', icon: 'check-done-01' },
  { key: 'account', label: 'Account emails', icon: 'user-square' },
  { key: 'layouts', label: 'Layouts', icon: 'sticker-square' },
]

// ---------------------------------------------------------------------------
// Scoped overrides (the polymorphic `owner` on mailer_templates)
//
// An override is a *whole row*, exactly as `MailerTemplate.resolve` treats it
// today: it stops following the SupporterBase default entirely. The UI's job is
// therefore to make that consequence obvious rather than to hide it. Per-field
// inheritance is kept as an optional extension in the design tools — it is the
// kinder admin experience, but it needs nullable-means-inherit on owned rows,
// a merge step in `resolve`, and relaxed presence validation.
//
// Each mailer key declares the single owner type it can be scoped by. As
// shipped in #6883 that is `EventType` for the shift mailers; a mailer that
// belongs to a group rather than an event will declare `group` instead. The two
// stay mutually exclusive per key, so `resolve` never needs a precedence chain.
// ---------------------------------------------------------------------------

/** Fields that can differ from the default. Shown as a diff; not stored per field. */
type OverridableField = 'subject' | 'preheader' | 'bodyHtml' | 'enabled' | 'fromName' | 'replyTo'

const OVERRIDABLE_FIELDS: OverridableField[] = ['subject', 'preheader', 'bodyHtml', 'enabled', 'fromName', 'replyTo']

const FIELD_LABELS: Record<OverridableField, string> = {
  subject: 'Subject',
  preheader: 'Preheader',
  bodyHtml: 'Body',
  enabled: 'On/off',
  fromName: 'From name',
  replyTo: 'Reply-to',
}

/** What a `mailer_templates.owner` can point at. */
type OwnerKind = 'event_type' | 'group'

type OverrideOwner = { id: string; name: string; kind: OwnerKind }

const OWNER_KIND_LABELS: Record<OwnerKind, string> = {
  event_type: 'Event types',
  group: 'Groups',
}

const OWNER_KIND_SINGULAR: Record<OwnerKind, string> = {
  event_type: 'event type',
  group: 'group',
}

type Override = {
  groupId: string
  groupName: string
  kind: OwnerKind
  /** `mailer_templates.updated_at`. There is no editor attribution on the table. */
  editedAt: string
  values: Partial<Pick<Draft, OverridableField>>
}

const EVENT_TYPE_NAMES = [
  'Doorknock', 'Phone bank', 'Community event', 'Street stall', 'Town hall', 'Fundraiser', 'Training session',
  'Volunteer social', 'Letterboxing', 'Petition drive', 'Candidate forum', 'Branch meeting', 'Rally',
  'Voter registration', 'Sausage sizzle', 'Film night', 'Committee meeting', 'Working bee', 'Listening post',
  'Membership drive', 'School visit', 'Market stall', 'Roundtable', 'Campaign launch', 'Postcard writing',
  'Text bank', 'AGM', 'Induction',
]

const GROUP_NAMES = [
  'Northside Volunteers', 'Fitzroy Chapter', 'Brunswick East', 'Coburg Crew', 'Docklands Team', 'Carlton Branch',
  'Preston Locals', 'Thornbury Group', 'Richmond Hub', 'Collingwood Collective', 'Footscray Friends',
  'Yarraville Crew', 'Williamstown Branch', 'Altona Organisers', 'Sunshine Supporters', 'Essendon Chapter',
  'Moonee Ponds', 'Pascoe Vale', 'Reservoir Team', 'Heidelberg Group', 'Ivanhoe Locals', 'Kew Branch',
]

const slugify = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

const OVERRIDE_OWNERS: OverrideOwner[] = [
  ...EVENT_TYPE_NAMES.map((name) => ({ id: slugify(name), name, kind: 'event_type' as const })),
  ...GROUP_NAMES.map((name) => ({ id: slugify(name), name, kind: 'group' as const })),
]

/** Sample overrides, keyed by mailer id. */
const OVERRIDES: Record<string, Override[]> = {
  shifts_changed: [
    {
      groupId: slugify('Doorknock'),
      groupName: 'Doorknock',
      kind: 'event_type',
      editedAt: '4 Jun 2026',
      values: { preheader: 'Your doorknock shift has moved' },
    },
    {
      groupId: slugify('Phone bank'),
      groupName: 'Phone bank',
      kind: 'event_type',
      editedAt: '12 Jun 2026',
      values: {
        subject: 'Heads up — your {{ event.name }} shift has moved',
        bodyHtml: `${GREETING}

<p style="margin:0;">Your shift at {{ event.name }} has changed. Dial in five minutes early so we can run through the script — the link is in the calendar invite.</p>

${RULE}

<p style="font-weight:600;margin:0 0 16px;">Your shifts, as they now stand</p>

${SHIFT_LIST}

${SIGN_OFF}`,
      },
    },
    {
      groupId: slugify('Street stall'),
      groupName: 'Street stall',
      kind: 'event_type',
      editedAt: '3 Jul 2026',
      values: { enabled: false },
    },
  ],
  new_supporter_autoresponse: [
    {
      groupId: slugify('Northside Volunteers'),
      groupName: 'Northside Volunteers',
      kind: 'group',
      editedAt: '28 May 2026',
      values: { subject: 'Welcome to Northside Volunteers' },
    },
    {
      groupId: slugify('Fitzroy Chapter'),
      groupName: 'Fitzroy Chapter',
      kind: 'group',
      editedAt: '2 Jun 2026',
      values: { subject: 'Ciao from the Fitzroy Chapter' },
    },
    {
      groupId: slugify('Brunswick East'),
      groupName: 'Brunswick East',
      kind: 'group',
      editedAt: '2 Jun 2026',
      values: { preheader: 'Here is what happens next' },
    },
    {
      groupId: slugify('Coburg Crew'),
      groupName: 'Coburg Crew',
      kind: 'group',
      editedAt: '19 Jun 2026',
      values: { enabled: false },
    },
  ],
}

const overridesFor = (mailerId: string): Override[] => OVERRIDES[mailerId] ?? []

// ---------------------------------------------------------------------------
// A very small Liquid approximation, just enough to make the preview live
// ---------------------------------------------------------------------------

type Scope = Record<string, unknown>

type Node =
  | { type: 'text'; value: string }
  | { type: 'block'; kind: 'for' | 'if'; expr: string; body: Node[] }

function lookup(path: string, scope: Scope): unknown {
  return path.split('.').reduce<unknown>((acc, part) => {
    if (acc == null) return undefined
    if (part === 'size') {
      if (Array.isArray(acc) || typeof acc === 'string') return acc.length
      return undefined
    }
    return (acc as Record<string, unknown>)[part]
  }, scope)
}

function parse(template: string): Node[] {
  const tagRe = /\{%-?\s*(for|if|endfor|endif)\s*([^%]*?)\s*-?%\}/g
  const root: Node[] = []
  const stack: Node[][] = [root]
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = tagRe.exec(template)) !== null) {
    const current = stack[stack.length - 1]
    if (match.index > cursor) current.push({ type: 'text', value: template.slice(cursor, match.index) })
    cursor = match.index + match[0].length

    if (match[1] === 'for' || match[1] === 'if') {
      const node: Node = { type: 'block', kind: match[1], expr: match[2], body: [] }
      current.push(node)
      stack.push(node.body)
    } else if (stack.length > 1) {
      stack.pop()
    }
  }

  if (cursor < template.length) stack[stack.length - 1].push({ type: 'text', value: template.slice(cursor) })
  return root
}

function interpolate(text: string, scope: Scope): string {
  return text.replace(/\{\{-?\s*([\w.]+)\s*-?\}\}/g, (_all, path: string) => {
    const value = lookup(path, scope)
    return value == null ? '' : String(value)
  })
}

function truthy(expr: string, scope: Scope): boolean {
  const comparison = /^([\w.]+)\s*(>|<|==|!=)\s*(.+)$/.exec(expr.trim())

  if (comparison) {
    const left = lookup(comparison[1], scope)
    const raw = comparison[3].trim().replace(/^['"]|['"]$/g, '')
    const right: unknown = /^-?\d+$/.test(raw) ? Number(raw) : raw

    switch (comparison[2]) {
      case '>':
        return Number(left ?? 0) > Number(right)
      case '<':
        return Number(left ?? 0) < Number(right)
      case '==':
        return String(left ?? '') === String(right)
      default:
        return String(left ?? '') !== String(right)
    }
  }

  const value = lookup(expr.trim(), scope)
  if (Array.isArray(value)) return value.length > 0
  return Boolean(value)
}

function evaluate(nodes: Node[], scope: Scope): string {
  return nodes
    .map((node) => {
      if (node.type === 'text') return interpolate(node.value, scope)

      if (node.kind === 'for') {
        const [name, , listPath] = node.expr.trim().split(/\s+/)
        const list = lookup(listPath ?? '', scope)
        if (!Array.isArray(list)) return ''
        return list.map((item) => evaluate(node.body, { ...scope, [name]: item })).join('')
      }

      return truthy(node.expr, scope) ? evaluate(node.body, scope) : ''
    })
    .join('')
}

const renderLiquid = (template: string, scope: Scope): string => evaluate(parse(template), scope)

const toPlainText = (html: string): string =>
  html
    .replace(/<(br|\/p|\/li|\/h1|hr)[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

// ---------------------------------------------------------------------------
// Sample data
// ---------------------------------------------------------------------------

type SampleData = {
  recipientFirstName: string
  eventName: string
  eventLocation: string
  shiftCount: number
  otherRsvpCount: number
  personName: string
  groupName: string
  instanceName: string
}

const DEFAULT_SAMPLE: SampleData = {
  recipientFirstName: 'Priya',
  eventName: 'Northside Community Day',
  eventLocation: 'Fitzroy Town Hall, 201 Napier St, Fitzroy',
  shiftCount: 2,
  otherRsvpCount: 1,
  personName: 'Sam Okafor',
  groupName: 'Northside Volunteers',
  instanceName: 'Common Ground',
}

const SHIFT_SAMPLES = [
  { role: 'Welcome desk', summary: 'Welcome desk — Saturday, 6 Sep, 9:00am to 11:30am', start_time: '9:00am', end_time: '11:30am' },
  { role: 'Stall setup', summary: 'Stall setup — Saturday, 6 Sep, 11:30am to 2:00pm', start_time: '11:30am', end_time: '2:00pm' },
  { role: 'Pack down', summary: 'Pack down — Saturday, 6 Sep, 4:00pm to 5:30pm', start_time: '4:00pm', end_time: '5:30pm' },
]

const OTHER_RSVP_SAMPLES = [
  {
    event: { name: 'Doorknock: Brunswick East' },
    shifts: [{ summary: 'Doorknocker — Sunday, 14 Sep, 10:00am to 12:00pm' }],
  },
  {
    event: { name: 'Phone bank: membership drive' },
    shifts: [{ summary: 'Caller — Tuesday, 23 Sep, 6:00pm to 8:00pm' }],
  },
]

function buildScope(sample: SampleData): Scope {
  return {
    recipient: { first_name: sample.recipientFirstName },
    user: { name_or_email: sample.personName },
    invitee: { name_or_email: sample.personName },
    inviter: { name_or_email: 'Alex Rivera' },
    requester: { name_or_email: 'Alex Rivera' },
    creator: { name: 'Alex Rivera', first_name: 'Alex', email: 'alex@example.org' },
    supporter: { name: sample.personName, first_name: sample.personName.split(' ')[0], last_name: sample.personName.split(' ')[1] ?? '' },
    supporter_group: { name: sample.groupName },
    supporter_group_names: sample.groupName,
    event: {
      name: sample.eventName,
      url: 'https://example.org/events/northside-community-day',
      location: sample.eventLocation,
      full_venue_address: sample.eventLocation,
      date_and_time_details: 'Saturday, 6 Sep, 9:00am to 5:30pm',
      state: 'pending',
      approval_url: 'https://example.org/app/events/482/approve',
    },
    shifts: SHIFT_SAMPLES.slice(0, sample.shiftCount),
    other_upcoming_rsvps: OTHER_RSVP_SAMPLES.slice(0, sample.otherRsvpCount),
    blast: {
      subject: 'Three weeks to go — here is how you can help',
      body: 'Hi! We are doorknocking in Brunswick East this Sunday. Can you make it? Reply YES.',
      url: 'https://example.org/app/email_blasts/91',
    },
    invitation_url: 'https://example.org/users/invitation/accept?token=sample',
    reset_password_url: 'https://example.org/users/password/edit?token=sample',
    unlock_account_url: 'https://example.org/users/unlock?token=sample',
    unsubscribe_url: 'https://example.org/unsubscribe?token=sample',
    instance: { name: sample.instanceName, logo_url: '' },
  }
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'tw:relative tw:inline-flex tw:h-6 tw:w-11 tw:shrink-0 tw:rounded-full tw:transition',
        'tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300',
        disabled && 'tw:pointer-events-none tw:cursor-not-allowed tw:opacity-50',
        checked ? 'tw:bg-primary-600' : 'tw:bg-gray-300'
      )}
    >
      <span
        className={clsx(
          'tw:absolute tw:top-0.5 tw:h-5 tw:w-5 tw:rounded-full tw:bg-white tw:shadow tw:transition-all',
          checked ? 'tw:left-5.5' : 'tw:left-0.5'
        )}
      />
    </button>
  )
}

function SegControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</span>
      <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((option, index) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            aria-pressed={value === option.value}
            className={clsx(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              index > 0 && 'tw:border-l tw:border-gray-300',
              value === option.value ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * A product-side segmented control: one subject, several views of it. Used for
 * the view switch and for the preview's format and device toggles, which were
 * three copies of the same markup.
 */
function ViewToggle({
  label,
  value,
  options,
  onChange,
  size = 'md',
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
  size?: 'sm' | 'md'
}) {
  return (
    <div role="group" aria-label={label} className="tw:inline-flex tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={clsx(
            'tw:font-medium',
            size === 'sm' ? 'tw:px-2.5 tw:py-1 tw:text-xs' : 'tw:px-3 tw:py-1.5 tw:text-sm',
            index > 0 && 'tw:border-l tw:border-gray-300',
            value === option.value ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function StatusPills({
  mailer,
  enabled,
  overrideCount,
}: {
  mailer: Mailer
  enabled: boolean
  overrideCount: number
}) {
  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
      {!mailer.migrated && (
        <Badge color="warning" bordered>
          Not migrated yet
        </Badge>
      )}
      {mailer.customised ? (
        <Badge color="primary" bordered>
          Customised
        </Badge>
      ) : (
        // The same badge as its neighbours, in grey: `neutral` rather than
        // `gray`, whose gray-50 fill reads as no fill at all next to them.
        <Badge color="neutral" bordered>
          Using default
        </Badge>
      )}
      {mailer.suppressible && !enabled && (
        <Badge color="error" bordered>
          Switched off
        </Badge>
      )}
      {overrideCount > 0 && mailer.overrideScope && (
        <Badge color="info" bordered>
          {overrideCount} {OWNER_KIND_SINGULAR[mailer.overrideScope]} {overrideCount === 1 ? 'override' : 'overrides'}
        </Badge>
      )}
    </div>
  )
}

const emailPage = (html: string) =>
  `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;font-family:Inter,Helvetica,Arial,sans-serif;}</style></head><body>${html}</body></html>`

/**
 * Renders the composed email in a sandboxed iframe so the mailer's own styles
 * cannot leak into (or inherit from) the admin UI — which is also how it will
 * actually look in an inbox.
 *
 * It grows to its content rather than filling a pane, so the page scrolls as
 * one instead of trapping the email in a box with its own scrollbar. Measuring
 * that content needs `allow-same-origin`; scripts stay blocked, which is what
 * matters for HTML an admin has pasted in.
 */
function EmailPreview({ html, width }: { html: string; width: number | null }) {
  const frame = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(480)

  /**
   * Collapse the frame before reading it, or the measurement feeds on itself:
   * the document element stretches to whatever height we last set, and so
   * reports that height straight back.
   */
  const measure = () => {
    const element = frame.current
    const body = element?.contentDocument?.body
    if (!element || !body) return

    element.style.height = '0px'
    const next = Math.max(240, body.scrollHeight)
    element.style.height = `${next}px`
    setHeight(next)
  }

  useEffect(measure, [html, width])

  return (
    <div className="tw:flex tw:justify-center tw:bg-gray-cool-100 tw:p-4">
      <iframe
        ref={frame}
        title="Email preview"
        sandbox="allow-same-origin"
        srcDoc={emailPage(html)}
        onLoad={measure}
        className="tw:w-full tw:rounded-lg tw:border tw:border-gray-cool-200 tw:bg-white"
        style={{ height, ...(width ? { maxWidth: `${width}px` } : {}) }}
      />
    </div>
  )
}

/**
 * Picking which owner to override, at a scale a dropdown cannot carry.
 *
 * An instance can easily have dozens of event types, and later hundreds of
 * groups, so this is a searchable panel listing every owner with its current
 * state, multi-select so several can be added at once. It is the only shape
 * that also answers "which of my 40 event types are still on the default?" —
 * which is why it won over an inline type-ahead and a plain dropdown.
 */
function OwnerPickerSheet({
  open,
  onOpenChange,
  owners,
  overriddenIds,
  kind,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  owners: OverrideOwner[]
  overriddenIds: string[]
  kind: OwnerKind
  onAdd: (ids: string[]) => void
}) {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string[]>([])

  const matching = owners.filter((owner) => owner.name.toLowerCase().includes(query.trim().toLowerCase()))
  const label = OWNER_KIND_LABELS[kind].toLowerCase()

  const toggle = (id: string) =>
    setSelected((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]))

  const close = () => {
    setQuery('')
    setSelected([])
    onOpenChange(false)
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
        else onOpenChange(true)
      }}
    >
      <SheetContent className="tw:w-120">
        <SheetTitle>Add an override</SheetTitle>
        <div className="tw:flex tw:h-full tw:flex-col">
          <div className="tw:border-b tw:border-gray-cool-200 tw:p-6 tw:pb-4">
            <p className="tw:mb-3 tw:text-sm tw:text-gray-cool-600">
              Choose the {label} that should send their own copy of this mailer. {overriddenIds.length} of {owners.length} already
              do.
            </p>
            <div className="tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-gray-400 tw:bg-white tw:px-3 tw:py-2 tw:focus-within:border-primary-400 tw:focus-within:ring-4 tw:focus-within:ring-primary-100">
              <Icon type="search-lg" width={20} height={20} />
              <input
                type="search"
                autoFocus
                value={query}
                aria-label={`Search ${label}`}
                placeholder={`Search ${label}`}
                onChange={(event) => setQuery(event.target.value)}
                className="tw:w-full tw:text-base tw:placeholder-gray-500 tw:focus:outline-hidden"
              />
            </div>
          </div>

          <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-3 tw:py-2">
            {matching.length === 0 ? (
              <p className="tw:p-3 tw:text-sm tw:text-gray-cool-500">Nothing matches &ldquo;{query}&rdquo;.</p>
            ) : (
              <ul className="tw:flex tw:flex-col">
                {matching.map((owner) => {
                  const already = overriddenIds.includes(owner.id)

                  return (
                    <li key={owner.id}>
                      <label
                        className={clsx(
                          'tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:px-3 tw:py-2',
                          already ? 'tw:opacity-60' : 'tw:hover:bg-gray-cool-50'
                        )}
                      >
                        <input
                          type="checkbox"
                          disabled={already}
                          checked={already || selected.includes(owner.id)}
                          onChange={() => toggle(owner.id)}
                          className="tw:h-4 tw:w-4 tw:rounded tw:border-gray-400 tw:text-primary-600"
                        />
                        <span className="tw:flex-1 tw:text-sm tw:text-gray-cool-900">{owner.name}</span>
                        {already && <Badge color="info">Already overridden</Badge>}
                      </label>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="tw:flex tw:items-center tw:gap-2 tw:border-t tw:border-gray-cool-200 tw:p-6 tw:py-4">
            <span className="tw:text-sm tw:text-gray-cool-600">
              {selected.length === 0 ? 'None selected' : `${selected.length} selected`}
            </span>
            <div className="tw:ml-auto tw:flex tw:gap-2">
              <Button rank="secondary" size="sm" onClick={close}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={selected.length === 0}
                onClick={() => {
                  onAdd(selected)
                  close()
                }}
              >
                {selected.length === 0 ? 'Add overrides' : `Add ${selected.length} override${selected.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Every override in the SupporterBase, in one place — reached from the
 * catalogue rather than from inside a mailer, because "what have we changed,
 * and for whom" is a question about the whole instance rather than about the
 * mailer you happen to have open.
 *
 * The edit links are dead: they stand for a route to the override's own editor,
 * which does not exist yet. Buttons rather than `<a href="#">`, so a click does
 * nothing instead of jumping to the top of the page.
 */
function OverridesIndexSheet({
  open,
  onOpenChange,
  entries,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  entries: Array<{ mailer: Mailer; overrides: Override[] }>
  onAdd: (mailerId: string) => void
}) {
  const total = entries.reduce((count, entry) => count + entry.overrides.length, 0)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="tw:w-136">
        <SheetTitle>Overrides</SheetTitle>
        <div className="tw:flex tw:flex-col tw:gap-5 tw:overflow-y-auto tw:p-6">
          <p className="tw:text-sm tw:text-gray-cool-600">
            {total === 0
              ? 'Nothing overrides a SupporterBase default yet — every mailer sends the same copy to everyone.'
              : `${total} ${total === 1 ? 'owner sends its' : 'owners send their'} own copy of a mailer instead of the SupporterBase default.`}
          </p>

          {entries.map(({ mailer, overrides }) => (
            <section key={mailer.id}>
              <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                <h3 className="tw:text-sm tw:font-bold tw:text-gray-cool-900">{mailer.name}</h3>
                <Badge color="gray">{overrides.length}</Badge>
                <Button rank="secondary" size="sm" leadingIcon="plus" className="tw:ml-auto" onClick={() => onAdd(mailer.id)}>
                  Add an override
                </Button>
              </div>

              <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-cool-100 tw:rounded-lg tw:border tw:border-gray-cool-200">
                {overrides.map((override) => {
                  const changed = OVERRIDABLE_FIELDS.filter((field) => override.values[field] !== undefined)

                  return (
                    <li key={override.groupId} className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:px-3 tw:py-2.5">
                      <span className="tw:min-w-0 tw:flex-1">
                        <span className="tw:block tw:text-sm tw:font-medium tw:text-gray-cool-900">{override.groupName}</span>
                        <span className="tw:block tw:text-xs tw:text-gray-cool-500">
                          {OWNER_KIND_SINGULAR[override.kind]} ·{' '}
                          {changed.length > 0
                            ? `differs in ${changed.map((field) => FIELD_LABELS[field].toLowerCase()).join(', ')}`
                            : 'a copy of the default'}{' '}
                          · edited {override.editedAt}
                        </span>
                      </span>
                      {override.values.enabled === false && <Badge color="error">Switched off here</Badge>}
                      <Button rank="link" size="sm" onClick={() => undefined}>
                        Edit
                      </Button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * The body editor, with a plain-textarea fallback.
 *
 * The v2 layout still loads the v1 Sprockets bundle, which puts TinyMCE 5.10
 * (from the `tinymce-rails` gem) on `window.tinymce`. `@tinymce/tinymce-react`
 * uses that global in preference to loading its own copy, and TinyMCE 5 then
 * fails because its lazily-fetched theme and plugin assets 404. Rather than
 * leave a blank hole in a page that exists to be reviewed, fall back to editing
 * the raw HTML if the editor has not initialised shortly after mount.
 */
function BodyEditor({
  editorKey,
  value,
  disabled,
  onChange,
}: {
  editorKey: string
  value: string
  disabled: boolean
  onChange: (content: string) => void
}) {
  const [loading, setLoading] = useState(true)
  const [gaveUp, setGaveUp] = useState(false)

  useEffect(() => {
    setLoading(true)
    setGaveUp(false)
    const timer = window.setTimeout(() => setGaveUp(true), 2500)
    return () => window.clearTimeout(timer)
  }, [editorKey])

  if (gaveUp && loading) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <p className="tw:flex tw:items-start tw:gap-2 tw:rounded-lg tw:bg-warning-50 tw:px-3 tw:py-2 tw:text-xs tw:text-warning-700">
          <Icon type="alert-triangle" className="tw:mt-0.5 tw:h-3.5 tw:w-3.5 tw:shrink-0" />
          The rich text editor could not start, so this is the raw HTML. TinyMCE 5.10 from the legacy asset bundle is claiming{' '}
          <code className="tw:font-mono">window.tinymce</code> and its theme assets are missing — a plumbing problem, not part of this
          design.
        </p>
        <textarea
          aria-label="Body HTML"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="tw:min-h-[280px] tw:w-full tw:rounded-lg tw:border tw:border-gray-400 tw:p-3 tw:font-mono tw:text-xs tw:focus:border-primary-400 tw:focus:outline-hidden tw:focus:ring-4 tw:focus:ring-primary-100 tw:disabled:bg-gray-100"
        />
      </div>
    )
  }

  return (
    <RichTextEditor
      key={editorKey}
      id={`body-${editorKey}`}
      name={`body-${editorKey}`}
      value={value}
      disabled={disabled}
      minHeight={280}
      // High enough that the editor grows with the body instead of scrolling
      // inside itself, which would be a second scroll area on the page.
      maxHeight={2400}
      setIsLoading={setLoading}
      isLoading={loading}
      onChange={onChange}
    />
  )
}

/**
 * Per-field override control — only used by the optional per-field granularity
 * explored in the design tools. With whole-row overrides (the default) there is
 * nothing per field to say, so this does not render.
 *
 * Building this for real means nullable-means-inherit on owned rows, a merge in
 * `MailerTemplate.resolve`, and relaxing the subject/body presence validation
 * for non-instance-wide rows.
 */
function FieldScopeNote({
  overridden,
  ownerName,
  onOverride,
  onRevert,
  disabled,
}: {
  overridden: boolean
  ownerName: string
  onOverride: () => void
  onRevert: () => void
  disabled?: boolean
}) {
  return (
    <div className="tw:mb-1 tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:text-xs">
      {overridden ? (
        <>
          <Badge color="info">Overridden for {ownerName}</Badge>
          <button
            type="button"
            disabled={disabled}
            onClick={onRevert}
            className="tw:font-medium tw:text-primary-700 tw:underline tw:disabled:text-gray-400 tw:disabled:no-underline"
          >
            Use the default again
          </button>
        </>
      ) : (
        <>
          <span className="tw:flex tw:items-center tw:gap-1 tw:text-gray-cool-500">
            <Icon type="arrow-circle-broken-right" className="tw:h-3.5 tw:w-3.5" />
            Inherited from the default
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={onOverride}
            className="tw:font-medium tw:text-primary-700 tw:underline tw:disabled:text-gray-400 tw:disabled:no-underline"
          >
            Override for {ownerName}
          </button>
        </>
      )}
    </div>
  )
}

function ScrollableMenu({ children }: { children: ReactNode }) {
  // overflow-x-hidden is deliberate: setting only overflow-y makes the computed
  // overflow-x `auto`, which gives long tokens a stray sideways scrollbar.
  return <div className="tw:max-h-80 tw:overflow-y-auto tw:overflow-x-hidden">{children}</div>
}

function SmartFieldList({
  groups,
  onInsert,
  variant,
  fieldLabel,
}: {
  groups: SmartFieldGroup[]
  onInsert: (token: string) => void
  variant: 'panel' | 'menu'
  /** The field this menu inserts into. Named in the trigger so the target is never ambiguous. */
  fieldLabel?: string
}) {
  if (variant === 'menu') {
    /* A lone asterisk here read as a required-field marker — the one glyph a
       form label must not borrow — so every trigger is the same labelled
       control, whichever field it belongs to. */
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            rank="secondary"
            size="sm"
            leadingIcon="plus"
            trailingIcon="chevron-down"
            ariaLabel={`Insert a smart field into ${fieldLabel}`}
          >
            Smart field
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <ScrollableMenu>
          {fieldLabel && <DropdownMenuLabel>Insert into {fieldLabel.toLowerCase()}</DropdownMenuLabel>}
          {groups.map((group, index) => (
            <div key={group.label}>
              {(index > 0 || fieldLabel) && <DropdownMenuSeparator />}
              <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
              {group.fields.map((field) => (
                <DropdownMenuItem key={field.token} onSelect={() => onInsert(field.token)}>
                  <span className="tw:flex tw:flex-col">
                    <span className="tw:text-sm tw:text-gray-700">{field.label}</span>
                    <span className="tw:font-mono tw:text-xs tw:text-gray-500">{field.token}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </div>
          ))}
            </ScrollableMenu>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="tw:mb-1.5 tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-cool-400">{group.label}</p>
          <div className="tw:flex tw:flex-wrap tw:gap-1.5">
            {group.fields.map((field) => (
              <TooltipProvider key={field.token}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onInsert(field.token)}
                      className="tw:rounded-full tw:border tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:px-2.5 tw:py-1 tw:font-mono tw:text-xs tw:text-gray-cool-600 tw:hover:border-primary-400 tw:hover:bg-primary-50 tw:hover:text-primary-700"
                    >
                      {field.label}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className="tw:font-mono tw:text-xs">{field.token}</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The catalogue
//
// Committed: the index is the landing view and the editor is opened from it,
// the shape `group_inbox_generic_preview` uses for conversation threads. It
// costs a click to reach any mailer and gives back the one view that answers
// "what do we send, and what have we changed?".
//
// How that catalogue is laid out is still open, so three treatments are kept
// side by side in the design tools: `list` rows, a `table` with column headers,
// and `cards` grouped by type.
// ---------------------------------------------------------------------------

type IndexLayout = 'list' | 'table' | 'cards'

/**
 * How the type filter is offered. Pills show every type and its count at a
 * glance and spend a whole row doing it; the dropdown costs one control and
 * makes you open it to see what is there.
 */
type FilterStyle = 'dropdown' | 'pills'

/** How the editor opens: the copy, its rendering, or both at once. */
type OpenMode = 'edit' | 'preview' | 'split'

type NavItem = Mailer | EmailLayout

type NavGroup = { key: GroupKey; label: string; icon: IconTypes; items: NavItem[] }

const asMailer = (item: NavItem): Mailer | null => ('group' in item ? item : null)

const overridesLabel = (count: number) => (count > 0 ? `${count} override${count === 1 ? '' : 's'}` : 'No overrides')

/** What has been done to a mailer, wherever it is listed. */
function MailerBadges({ item, signposted }: { item: NavItem; signposted: boolean }) {
  const mailer = asMailer(item)

  return (
    <>
      {mailer?.customised && <Badge color="primary">Customised</Badge>}
      {signposted && !mailer?.migrated && <Badge color="warning">Not migrated</Badge>}
    </>
  )
}

/**
 * One button, opening the side-by-side view — where the copy and its rendering
 * sit together, and the view control switches between them. Reading and
 * changing were two buttons for one destination; the destination now answers
 * both, so the row only has to get you there.
 *
 * Labelled "Customise" — see the note in the page docblock.
 */
function RowAction({ id, onOpen }: { id: string; onOpen: (id: string, mode: OpenMode) => void }) {
  return (
    <Button rank="secondary" size="sm" leadingIcon="pencil-02" onClick={() => onOpen(id, 'split')}>
      Customise
    </Button>
  )
}

/**
 * The sending switch, in the list itself. Switching a mailer off is the most
 * common thing an admin comes here to do, and it needs neither the editor nor a
 * save — so it applies where it is read.
 *
 * A mailer that cannot be suppressed shows the switch anyway, on and disabled,
 * with the reason on hover: a blank space says only "no control here" and
 * leaves an admin to wonder whether it is missing or forbidden.
 */
function RowToggle({
  item,
  isEnabled,
  onToggleEnabled,
}: {
  item: NavItem
  isEnabled: (id: string) => boolean
  onToggleEnabled: (id: string, value: boolean) => void
}) {
  const mailer = asMailer(item)

  // A layout is not sent to anyone, so it has no sending state to show.
  if (!mailer) {
    // Matches the toggle's w-11, so names stay in one column either way.
    return <span className="tw:w-11 tw:shrink-0" aria-hidden />
  }

  if (!mailer.suppressible) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* The switch is disabled, so it dispatches no pointer events of
                its own — the wrapper is what the tooltip listens to, and what
                a keyboard reaches. */}
            <span tabIndex={0} className="tw:shrink-0 tw:cursor-not-allowed">
              <Toggle
                checked={isEnabled(mailer.id)}
                disabled
                onChange={() => undefined}
                label={`${mailer.name} is always sent and cannot be switched off`}
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Required — this mailer is part of how the product works, so it cannot be switched off.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Toggle
      checked={isEnabled(mailer.id)}
      onChange={(value) => onToggleEnabled(mailer.id, value)}
      label={`Send the ${mailer.name} mailer`}
    />
  )
}

type IndexProps = {
  groups: NavGroup[]
  layout: IndexLayout
  filterStyle: FilterStyle
  search: string
  typeFilter: GroupKey | 'all'
  signposted: boolean
  /** Overrides are worth a column only while a mailer can be scoped per owner. */
  showOverrides: boolean
  overrideCount: (id: string) => number
  onManageOverrides: () => void
  isEnabled: (id: string) => boolean
  onToggleEnabled: (id: string, value: boolean) => void
  onSearch: (value: string) => void
  onTypeFilter: (value: GroupKey | 'all') => void
  onOpen: (id: string, mode: OpenMode) => void
}

function MailerIndex({
  groups,
  layout,
  filterStyle,
  search,
  typeFilter,
  signposted,
  showOverrides,
  overrideCount,
  isEnabled,
  onToggleEnabled,
  onManageOverrides,
  onSearch,
  onTypeFilter,
  onOpen,
}: IndexProps) {
  const shown = groups.filter((group) => typeFilter === 'all' || group.key === typeFilter)
  const rows = shown.flatMap((group) => group.items.map((item) => ({ item, group })))
  const total = groups.reduce((count, group) => count + group.items.length, 0)

  const filters: Array<{ key: GroupKey | 'all'; label: string; icon: IconTypes | null; count: number }> = [
    { key: 'all', label: 'All types', icon: null, count: total },
    ...groups.map((group) => ({ key: group.key, label: group.label, icon: group.icon, count: group.items.length })),
  ]
  const selectedFilter = filters.find((filter) => filter.key === typeFilter) ?? filters[0]

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
        <div className="tw:flex tw:w-72 tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-gray-400 tw:bg-white tw:px-3 tw:py-2 tw:focus-within:border-primary-400 tw:focus-within:ring-4 tw:focus-within:ring-primary-100">
          <Icon type="search-lg" width={20} height={20} />
          <input
            type="search"
            value={search}
            aria-label="Search mailers"
            placeholder="Search mailers"
            onChange={(event) => onSearch(event.target.value)}
            className="tw:w-full tw:text-base tw:font-normal tw:placeholder-gray-500 tw:focus:outline-hidden"
          />
        </div>

        {filterStyle === 'pills' ? (
          <div role="group" aria-label="Filter by type" className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
            {filters.map((filter) => {
              const active = typeFilter === filter.key

              return (
                <button
                  key={filter.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onTypeFilter(filter.key)}
                  className={clsx(
                    'tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:px-3 tw:py-1 tw:text-sm tw:font-medium',
                    active
                      ? 'tw:border-primary-200 tw:bg-primary-50 tw:text-primary-700'
                      : 'tw:border-gray-cool-200 tw:bg-white tw:text-gray-cool-600 tw:hover:border-primary-300 tw:hover:text-primary-700'
                  )}
                >
                  {filter.icon && <Icon type={filter.icon} className="tw:h-3.5 tw:w-3.5" aria-hidden />}
                  {filter.label}
                  <span className="tw:text-xs tw:font-normal tw:text-gray-cool-400">{filter.count}</span>
                </button>
              )
            })}
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button rank="secondary" leadingIcon="filter-lines" trailingIcon="chevron-down">
                {selectedFilter.label}
                <span className="tw:text-xs tw:font-normal tw:text-gray-cool-400">{selectedFilter.count}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Filter by type</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={typeFilter} onValueChange={(value) => onTypeFilter(value as GroupKey | 'all')}>
                {filters.map((filter) => (
                  <DropdownMenuRadioItem key={filter.key} value={filter.key}>
                    <span className="tw:flex tw:w-44 tw:items-center tw:gap-2">
                      {filter.icon && <Icon type={filter.icon} className="tw:h-3.5 tw:w-3.5" aria-hidden />}
                      {filter.label}
                      <span className="tw:ml-auto tw:text-xs tw:text-gray-cool-400">{filter.count}</span>
                    </span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {showOverrides && (
          <Button rank="secondary" leadingIcon="users-01" className="tw:ml-auto" onClick={onManageOverrides}>
            Manage overrides
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white tw:p-10 tw:text-center tw:text-sm tw:text-gray-cool-500">
          No mailers match that search.
        </p>
      ) : layout === 'cards' ? (
        <IndexCards
          groups={shown}
          signposted={signposted}
          showOverrides={showOverrides}
          overrideCount={overrideCount}
          isEnabled={isEnabled}
          onToggleEnabled={onToggleEnabled}
          onOpen={onOpen}
        />
      ) : layout === 'table' ? (
        <IndexTable
          rows={rows}
          signposted={signposted}
          showOverrides={showOverrides}
          overrideCount={overrideCount}
          isEnabled={isEnabled}
          onToggleEnabled={onToggleEnabled}
          onOpen={onOpen}
        />
      ) : (
        <IndexList
          rows={rows}
          signposted={signposted}
          showOverrides={showOverrides}
          overrideCount={overrideCount}
          isEnabled={isEnabled}
          onToggleEnabled={onToggleEnabled}
          onOpen={onOpen}
        />
      )}
    </div>
  )
}

type RowsProps = Pick<
  IndexProps,
  'signposted' | 'showOverrides' | 'overrideCount' | 'isEnabled' | 'onToggleEnabled' | 'onOpen'
> & {
  rows: Array<{ item: NavItem; group: NavGroup }>
}

/** Rows on one surface: the least chrome, and the type has to be repeated. */
function IndexList({ rows, signposted, showOverrides, overrideCount, isEnabled, onToggleEnabled, onOpen }: RowsProps) {
  return (
    <div className="tw:overflow-hidden tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white">
      <ul>
        {rows.map(({ item, group }, index) => (
          <li
            key={item.id}
            className={clsx('tw:flex tw:items-center tw:gap-4 tw:px-5 tw:py-3.5', index > 0 && 'tw:border-t tw:border-gray-cool-200')}
          >
            <RowToggle item={item} isEnabled={isEnabled} onToggleEnabled={onToggleEnabled} />

            <span className="tw:min-w-0 tw:flex-1">
              <span className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                <span className="tw:truncate tw:font-medium tw:text-gray-cool-900">{item.name}</span>
                <MailerBadges item={item} signposted={signposted} />
              </span>
              <span className="tw:mt-0.5 tw:block tw:truncate tw:text-sm tw:text-gray-cool-600">
                {'description' in item ? item.description : ''}
              </span>
            </span>

            <span className="tw:hidden tw:w-32 tw:shrink-0 tw:items-center tw:gap-1.5 tw:text-sm tw:text-gray-cool-500 tw:lg:flex">
              <Icon type={group.icon} className="tw:h-3.5 tw:w-3.5" aria-hidden />
              {group.label}
            </span>

            {showOverrides && (
              <span className="tw:hidden tw:w-28 tw:shrink-0 tw:text-sm tw:text-gray-cool-500 tw:lg:block">
                {overridesLabel(overrideCount(item.id))}
              </span>
            )}

            <RowAction id={item.id} onOpen={onOpen} />
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The same rows under column headers, which name what each column is. */
function IndexTable({ rows, signposted, showOverrides, overrideCount, isEnabled, onToggleEnabled, onOpen }: RowsProps) {
  return (
    <div className="tw:overflow-x-auto tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white">
      <table className="tw:w-full tw:text-left">
        <thead className="tw:sticky tw:top-0 tw:bg-gray-cool-50">
          <tr className="tw:border-b tw:border-gray-cool-200 tw:text-xs tw:uppercase tw:tracking-wide tw:text-gray-cool-500">
            <th scope="col" className="tw:px-5 tw:py-2.5 tw:font-medium">
              Activated
            </th>
            <th scope="col" className="tw:px-3 tw:py-2.5 tw:font-medium">
              Mailer
            </th>
            <th scope="col" className="tw:px-3 tw:py-2.5 tw:font-medium">
              Type
            </th>
            {showOverrides && (
              <th scope="col" className="tw:px-3 tw:py-2.5 tw:font-medium">
                Overrides
              </th>
            )}
            <th scope="col" className="tw:px-5 tw:py-2.5 tw:text-right tw:font-medium">
              <span className="tw:sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ item, group }) => (
            <tr key={item.id} className="tw:border-b tw:border-gray-cool-200 tw:last:border-0">
              <td className="tw:px-5 tw:py-3">
                <RowToggle item={item} isEnabled={isEnabled} onToggleEnabled={onToggleEnabled} />
              </td>
              <td className="tw:min-w-[18rem] tw:px-3 tw:py-3">
                <span className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                  <span className="tw:font-medium tw:text-gray-cool-900">{item.name}</span>
                  <MailerBadges item={item} signposted={signposted} />
                </span>
                <span className="tw:mt-0.5 tw:block tw:text-sm tw:text-gray-cool-600">
                  {'description' in item ? item.description : ''}
                </span>
              </td>
              <td className="tw:whitespace-nowrap tw:px-3 tw:py-3 tw:text-sm tw:text-gray-cool-500">
                <span className="tw:flex tw:items-center tw:gap-1.5">
                  <Icon type={group.icon} className="tw:h-3.5 tw:w-3.5" aria-hidden />
                  {group.label}
                </span>
              </td>
              {showOverrides && (
                <td className="tw:whitespace-nowrap tw:px-3 tw:py-3 tw:text-sm tw:text-gray-cool-500">
                  {overridesLabel(overrideCount(item.id))}
                </td>
              )}
              <td className="tw:px-5 tw:py-3">
                <span className="tw:flex tw:justify-end">
                  <RowAction id={item.id} onOpen={onOpen} />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * One card per type, so the group is the heading and never repeats down a
 * column. Reads well while the catalogue is this size; a card per type stops
 * paying once a type holds more mailers than fits without its own scroll.
 */
function IndexCards({
  groups,
  signposted,
  showOverrides,
  overrideCount,
  isEnabled,
  onToggleEnabled,
  onOpen,
}: Pick<
  IndexProps,
  'groups' | 'signposted' | 'showOverrides' | 'overrideCount' | 'isEnabled' | 'onToggleEnabled' | 'onOpen'
>) {
  return (
    <div>
      <div className="tw:grid tw:gap-4 tw:xl:grid-cols-2">
        {groups.map((group) => (
          <section key={group.key} className="tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white tw:p-5">
            <h2 className="tw:flex tw:items-center tw:gap-2 tw:text-lg tw:font-bold tw:text-gray-cool-900">
              <Icon type={group.icon} className="tw:h-4 tw:w-4 tw:text-gray-cool-400" aria-hidden />
              {group.label}
            </h2>
            <p className="tw:mb-2 tw:mt-1 tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-cool-400">Activated</p>

            <ul className="tw:flex tw:flex-col">
              {group.items.map((item, index) => (
                <li
                  key={item.id}
                  className={clsx(
                    'tw:flex tw:items-start tw:gap-4 tw:py-3',
                    index > 0 && 'tw:border-t tw:border-gray-cool-200'
                  )}
                >
                  <span className="tw:pt-0.5">
                    <RowToggle item={item} isEnabled={isEnabled} onToggleEnabled={onToggleEnabled} />
                  </span>

                  <span className="tw:min-w-0 tw:flex-1">
                    <span className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                      <span className="tw:font-medium tw:text-gray-cool-900">{item.name}</span>
                      <MailerBadges item={item} signposted={signposted} />
                    </span>
                    <span className="tw:mt-0.5 tw:block tw:text-sm tw:text-gray-cool-600">
                      {'description' in item ? item.description : ''}
                    </span>
                    {showOverrides && (
                      <span className="tw:mt-1 tw:block tw:text-xs tw:text-gray-cool-500">
                        {overridesLabel(overrideCount(item.id))}
                      </span>
                    )}
                  </span>

                  <RowAction id={item.id} onOpen={onOpen} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type MigrationStage = 'shipping' | 'signposted' | 'complete'

type Draft = { subject: string; preheader: string; bodyHtml: string; enabled: boolean; layoutId: string; fromName: string; replyTo: string }

type EditorTarget = 'subject' | 'preheader' | 'body'

/**
 * Side by side is not a preference, it is a question of whether there is room:
 * below this the two panes would each be too narrow to work in, so the editor
 * shows one at a time and offers the switch. Above it, both fit and the switch
 * has nothing left to decide.
 */
const SIDE_BY_SIDE_FROM = '(min-width: 1280px)'

function useRoomForBoth() {
  const [matches, setMatches] = useState(() => window.matchMedia(SIDE_BY_SIDE_FROM).matches)

  useEffect(() => {
    const query = window.matchMedia(SIDE_BY_SIDE_FROM)
    const update = () => setMatches(query.matches)

    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  return matches
}

const tinymceEditor = () =>
  (window as unknown as { tinymce?: { activeEditor?: { insertContent?: (content: string) => void } | null } }).tinymce?.activeEditor

const MailersSettingsPreview = () => {
  // Design-explorer state
  const [showDesign, setShowDesign] = useState(false)
  const roomForBoth = useRoomForBoth()
  const [smartFieldStyle, setSmartFieldStyle] = useState<'panel' | 'menu'>('menu')
  // The catalogue as it will be once every mailer is templated: the state the
  // design is for. The earlier stages stay in the design tools, not as the
  // default, so the layout is judged against the whole list rather than four.
  const [migration, setMigration] = useState<MigrationStage>('complete')
  const [overridesUI, setOverridesUI] = useState<'off' | 'index'>('off')
  const [overrideGranularity, setOverrideGranularity] = useState<'row' | 'field'>('row')
  const [indexLayout, setIndexLayout] = useState<IndexLayout>('table')
  /** The catalogue is the landing view; opening a mailer swaps in the editor. */
  const [browsing, setBrowsing] = useState(true)
  const [typeFilter, setTypeFilter] = useState<GroupKey | 'all'>('all')
  const [filterStyle, setFilterStyle] = useState<FilterStyle>('dropdown')
  /** How an admin gets from a mailer back to the catalogue. */
  const [wayBack, setWayBack] = useState<'breadcrumb' | 'button' | 'both'>('breadcrumb')

  // Product state
  const [selectedId, setSelectedId] = useState<string>('shift_slots_assigned')
  const [search, setSearch] = useState('')
  const [pane, setPane] = useState<'edit' | 'preview'>('edit')
  const [previewFormat, setPreviewFormat] = useState<'html' | 'text'>('html')
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [sampleOpen, setSampleOpen] = useState(false)
  const [sample, setSample] = useState<SampleData>(DEFAULT_SAMPLE)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [lastFocused, setLastFocused] = useState<EditorTarget>('body')

  // Which scope is being edited: the SupporterBase default, or one group's override.
  const [scopeGroupId, setScopeGroupId] = useState<string | null>(null)
  const [overrideValues, setOverrideValues] = useState<Record<string, Partial<Pick<Draft, OverridableField>>>>({})
  /** Overrides created in this session, and seeded ones reset in this session. */
  const [addedOverrides, setAddedOverrides] = useState<Record<string, boolean>>({})
  const [removedOverrides, setRemovedOverrides] = useState<Record<string, boolean>>({})
  /** Which mailer the owner picker is adding an override to, if any. */
  const [pickerMailerId, setPickerMailerId] = useState<string | null>(null)
  const [overridesIndexOpen, setOverridesIndexOpen] = useState(false)

  const subjectRef = useRef<HTMLInputElement>(null)
  const preheaderRef = useRef<HTMLInputElement>(null)

  const selectedLayout = LAYOUTS.find((layout) => layout.id === selectedId) ?? null
  const selectedMailer = MAILERS.find((mailer) => mailer.id === selectedId) ?? null
  const isLayout = selectedLayout !== null

  // Phase 1 = only the shift mailers from #6883/#6907 are editable here.
  const hidesUnmigrated = migration === 'shipping'
  const locked = migration === 'signposted' && !isLayout && selectedMailer !== null && !selectedMailer.migrated
  const layoutsLocked = migration === 'signposted' && isLayout

  // Overrides only make sense for mailers, and only for the owner type the mailer declares.
  const overrides = selectedMailer ? overridesFor(selectedMailer.id) : []
  const activeGroup = scopeGroupId ? OVERRIDE_OWNERS.find((owner) => owner.id === scopeGroupId) ?? null : null
  const overrideKey = `${selectedId}:${scopeGroupId ?? 'default'}`

  /**
   * Sending state lives here rather than in a draft: switched from the list it
   * applies where it is read, with no editor to open and nothing to save. The
   * editor's own toggle still edits the draft, so that one saves with the rest.
   */
  const [enabledById, setEnabledById] = useState<Record<string, boolean>>({})
  const isEnabled = (id: string) => enabledById[id] ?? MAILERS.find((mailer) => mailer.id === id)?.enabled ?? true
  const toggleEnabled = (id: string, value: boolean) => setEnabledById((current) => ({ ...current, [id]: value }))

  const defaults: Draft = useMemo(() => {
    if (selectedLayout) {
      return { subject: '', preheader: '', bodyHtml: selectedLayout.bodyHtml, enabled: true, layoutId: selectedLayout.id, fromName: '', replyTo: '' }
    }
    if (!selectedMailer) {
      return { subject: '', preheader: '', bodyHtml: '', enabled: true, layoutId: 'default', fromName: '', replyTo: '' }
    }
    return {
      subject: selectedMailer.subject,
      preheader: selectedMailer.preheader,
      bodyHtml: selectedMailer.bodyHtml,
      enabled: enabledById[selectedMailer.id] ?? selectedMailer.enabled,
      layoutId: selectedMailer.layoutId,
      fromName: '',
      replyTo: '',
    }
  }, [enabledById, selectedLayout, selectedMailer])

  /**
   * How this owner's copy differs from the default. Stored as a whole row —
   * this is only kept as a diff so the overrides card can say what changed.
   */
  const activeOverride: Partial<Pick<Draft, OverridableField>> = useMemo(() => {
    if (!scopeGroupId) return {}
    if (overrideValues[overrideKey]) return overrideValues[overrideKey]
    return overrides.find((entry) => entry.groupId === scopeGroupId)?.values ?? {}
  }, [overrideKey, overrideValues, overrides, scopeGroupId])

  // Whole-row: a full copy taken from the default when it was created.
  // Per-field: the default with only the owner's own fields layered on top.
  const baseline: Draft = useMemo(() => ({ ...defaults, ...activeOverride }), [activeOverride, defaults])

  const perField = overrideGranularity === 'field'
  const isOverridden = (field: OverridableField) => activeOverride[field] !== undefined
  /** True when this field is showing the default and cannot be typed into. */
  const inherits = (field: OverridableField) => perField && activeGroup !== null && !isOverridden(field)

  const setFieldOverridden = (field: OverridableField, overridden: boolean) => {
    const next = { ...activeOverride }
    if (overridden) {
      next[field] = defaults[field] as never
    } else {
      delete next[field]
    }
    setOverrideValues((current) => ({ ...current, [overrideKey]: next }))
    setDrafts((current) => {
      const remaining = { ...current }
      delete remaining[overrideKey]
      return remaining
    })
  }

  const draft = drafts[overrideKey] ?? baseline
  const dirty = drafts[overrideKey] !== undefined

  const update = (patch: Partial<Draft>) => {
    setDrafts((current) => ({ ...current, [overrideKey]: { ...draft, ...patch } }))

    if (!scopeGroupId) return
    setOverrideValues((current) => ({
      ...current,
      [overrideKey]: { ...activeOverride, ...(patch as Partial<Pick<Draft, OverridableField>>) },
    }))
  }

  const resetOverride = (ownerId: string) => {
    const key = `${selectedId}:${ownerId}`
    setRemovedOverrides((current) => ({ ...current, [key]: true }))
    setAddedOverrides((current) => ({ ...current, [key]: false }))
    setOverrideValues((current) => {
      const remaining = { ...current }
      delete remaining[key]
      return remaining
    })
    setDrafts((current) => {
      const remaining = { ...current }
      delete remaining[key]
      return remaining
    })
    if (scopeGroupId === ownerId) setScopeGroupId(null)
  }

  /** Create overrides as copies of that mailer's current default. */
  const addOverridesFor = (mailerId: string, ownerIds: string[]) => {
    setAddedOverrides((current) => ({
      ...current,
      ...Object.fromEntries(ownerIds.map((ownerId) => [`${mailerId}:${ownerId}`, true])),
    }))
    setRemovedOverrides((current) => {
      const remaining = { ...current }
      ownerIds.forEach((ownerId) => delete remaining[`${mailerId}:${ownerId}`])
      return remaining
    })
  }

  const discard = () =>
    setDrafts((current) => {
      const next = { ...current }
      delete next[overrideKey]
      return next
    })

  const selectMailer = (id: string, mode: OpenMode = 'split') => {
    setSelectedId(id)
    setScopeGroupId(null)
    // Side by side is what the row asks for, and what a wide screen gives it.
    // Narrower, it lands on the copy — the half there is room to work in.
    setPane(mode === 'preview' ? 'preview' : 'edit')
    setBrowsing(false)
  }


  /** Overrides as they now stand for any mailer, including this session's edits. */
  const liveOverrides = (mailerId: string): Override[] => {
    const seeded = overridesFor(mailerId)
      .filter((entry) => !removedOverrides[`${mailerId}:${entry.groupId}`])
      .map((entry) => ({ ...entry, values: overrideValues[`${mailerId}:${entry.groupId}`] ?? entry.values }))

    const added = Object.keys(addedOverrides)
      .filter((key) => key.startsWith(`${mailerId}:`) && addedOverrides[key])
      .map((key) => key.slice(mailerId.length + 1))
      .filter((ownerId) => !overridesFor(mailerId).some((entry) => entry.groupId === ownerId))
      .map((ownerId) => {
        const owner = OVERRIDE_OWNERS.find((entry) => entry.id === ownerId)
        return {
          groupId: ownerId,
          groupName: owner?.name ?? ownerId,
          kind: owner?.kind ?? 'group',
          editedAt: 'just now',
          values: overrideValues[`${mailerId}:${ownerId}`] ?? {},
        } satisfies Override
      })

    return [...seeded, ...added]
  }

  /** Every mailer that has one, for the catalogue-wide sheet. */
  const overrideEntries = MAILERS.filter((mailer) => mailer.overrideScope)
    .map((mailer) => ({ mailer, overrides: liveOverrides(mailer.id) }))
    .filter((entry) => entry.overrides.length > 0)

  const pickerMailer = MAILERS.find((mailer) => mailer.id === pickerMailerId) ?? null

  const scope = useMemo(() => buildScope(sample), [sample])

  const renderedBody = useMemo(() => renderLiquid(draft.bodyHtml, scope), [draft.bodyHtml, scope])
  const renderedSubject = useMemo(() => renderLiquid(draft.subject, scope), [draft.subject, scope])
  const renderedPreheader = useMemo(() => renderLiquid(draft.preheader, scope), [draft.preheader, scope])

  const composed = useMemo(() => {
    if (isLayout) return renderLiquid(draft.bodyHtml, { ...scope, content: '<p style="margin:0;">Your mailer content appears here.</p>' })
    const layout = LAYOUTS.find((entry) => entry.id === draft.layoutId) ?? LAYOUTS[0]
    return renderLiquid(layout.bodyHtml, { ...scope, content: renderedBody })
  }, [draft.bodyHtml, draft.layoutId, isLayout, renderedBody, scope])

  const smartFields = selectedLayout?.smartFields ?? selectedMailer?.smartFields ?? []

  /**
   * The email at full size, in its own tab. A blob URL rather than a route:
   * there is no server rendering these yet, and the point is to see the whole
   * thing at once rather than through a pane.
   */
  const openPreviewInNewTab = () => {
    const url = URL.createObjectURL(new Blob([emailPage(composed)], { type: 'text/html' }))
    window.open(url, '_blank', 'noopener')
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  /**
   * Insert at the cursor in a named field. The menu treatment passes its own
   * target so there is no hidden "wherever you were last" behaviour; the panel
   * treatment passes the last-focused field, which is the whole point of having
   * one shared palette.
   */
  const insertInto = (target: EditorTarget, token: string) => {
    if (target === 'body') {
      tinymceEditor()?.insertContent?.(token)
      return
    }

    const ref = target === 'subject' ? subjectRef : preheaderRef
    const input = ref.current
    const currentValue = target === 'subject' ? draft.subject : draft.preheader
    const at = input?.selectionStart ?? currentValue.length
    const next = `${currentValue.slice(0, at)}${token}${currentValue.slice(at)}`
    update(target === 'subject' ? { subject: next } : { preheader: next })
    input?.focus()
  }

  const matches = (mailer: Mailer) =>
    search.trim() === '' ||
    `${mailer.name} ${mailer.description}`.toLowerCase().includes(search.trim().toLowerCase())

  /** The catalogue as every navigation treatment needs it: groups, already filtered. */
  const navGroups: NavGroup[] = GROUPS.filter((group) => !(hidesUnmigrated && group.key === 'layouts'))
    .map((group) => ({
      ...group,
      items:
        group.key === 'layouts'
          ? LAYOUTS.filter((layout) => search.trim() === '' || layout.name.toLowerCase().includes(search.trim().toLowerCase()))
          : MAILERS.filter((mailer) => mailer.group === group.key && matches(mailer) && (!hidesUnmigrated || mailer.migrated)),
    }))
    .filter((group) => group.items.length > 0)

  const showsIndex = browsing
  /** The mailer joins the trail, and "Mailers" becomes the way up. */
  const crumbsLeadBack = !browsing && wayBack !== 'button'

  const showPreviewPane = roomForBoth || pane === 'preview'
  const showEditPane = roomForBoth || pane === 'edit'

  /**
   * Everything that acts on this mailer, in the bar below its name: next to the
   * switch between editing and reading they are plainly about the mailer in
   * hand, where the header's corner read more like page furniture.
   */
  const headerActions = (
    <div className="tw:flex tw:items-center tw:gap-2">
      {selectedMailer?.suppressible && (
        <div className="tw:flex tw:items-center tw:gap-2 tw:pr-2">
          <span className="tw:text-sm tw:text-gray-cool-600">Sending activated</span>
          <Toggle
            checked={draft.enabled}
            disabled={locked}
            onChange={(value) => update({ enabled: value })}
            label={`Send the ${selectedMailer.name} mailer${activeGroup ? ` for ${activeGroup.name}` : ''}`}
          />
        </div>
      )}
      <Button rank="secondary" size="sm" leadingIcon="send" onClick={() => undefined} disabled={locked}>
        Send test
      </Button>
    </div>
  )

  /**
   * The one action the overflow menu was hiding. It belongs beside the badge
   * that says whether there is anything to reset — the state and the action
   * that changes it are the same fact — rather than in the corner with the
   * controls that act on every mailer.
   *
   * "Manage overrides" is not here: with an inventory card in the editor, or a
   * scope switcher in the header, there was nothing left for it to do.
   */
  const usingDefault = !selectedMailer?.customised

  const resetToDefault = (
    <Button
      rank="secondary"
      size="sm"
      leadingIcon="refresh"
      disabled={usingDefault || locked}
      onClick={() => undefined}
      className={clsx(usingDefault && 'tw:pointer-events-none')}
    >
      Reset to default
    </Button>
  )

  /**
   * Edit and preview are two views of one mailer, not two sections of a page,
   * so the switch between them is a view control rather than a tab strip. It
   * only exists where there is no room for both at once — side by side, there
   * is nothing left for it to decide.
   */
  const paneSwitcher: ReactNode = roomForBoth ? null : (
    <ViewToggle
      label="View"
      value={pane}
      onChange={(value) => setPane(value as 'edit' | 'preview')}
      options={[
        { value: 'edit', label: 'Edit' },
        { value: 'preview', label: 'Preview' },
      ]}
    />
  )

  /**
   * The smart-field palette sits beside the editor rather than below it: with
   * the catalogue as the landing view there is no rail taking the width, and a
   * column of its own stays level with the field it inserts into.
   */
  const panelBeside = smartFieldStyle === 'panel' && showEditPane

  const smartFieldsPanel = (
    <div>
      {/* A column this narrow cannot hold the title, the target and the hint on
          one line, so they stack. */}
      <div className="tw:mb-3 tw:flex tw:flex-col tw:items-start tw:gap-2">
        <span className="tw:flex tw:items-center tw:gap-2">
          <Icon type="asterisk" className="tw:h-4 tw:w-4 tw:text-gray-cool-400" />
          <span className="tw:text-sm tw:font-bold tw:text-gray-cool-700">Smart fields</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} aria-label="What are smart fields?">
                  <Icon type="help-circle" className="tw:h-4 tw:w-4 tw:text-gray-cool-400" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Click one to drop it into whichever field you last had focused. They are filled in with real data when the email is
                sent.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
        <Badge color="primary">
          Inserting into {lastFocused === 'body' ? (isLayout ? 'layout HTML' : 'body') : lastFocused}
        </Badge>
      </div>
      <SmartFieldList groups={smartFields} onInsert={(token) => insertInto(lastFocused, token)} variant="panel" />
    </div>
  )

  /** Wrapped when disabled: a disabled button dispatches no pointer events. */
  const resetControl = usingDefault ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="tw:cursor-not-allowed">
            {resetToDefault}
          </span>
        </TooltipTrigger>
        <TooltipContent>Nothing to reset — this mailer is already the SupporterBase default.</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    resetToDefault
  )

  const saveBar = (
    <>
      {selectedMailer && resetControl}
      <span className="tw:text-sm tw:text-gray-cool-600">
        {dirty ? 'Unsaved changes' : 'All changes saved'}
        {activeGroup && <> · saving affects {activeGroup.name} only</>}
      </span>
      <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">
        <Button rank="secondary" size="sm" onClick={discard} disabled={!dirty}>
          Discard
        </Button>
        <Button size="sm" onClick={() => undefined} disabled={!dirty || locked}>
          Save changes
        </Button>
      </div>
    </>
  )

  return (
    <PageLayout.Root>
      <div className="tw:px-6 tw:pt-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/app/settings">Settings</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {crumbsLeadBack ? (
                <BreadcrumbLink asChild>
                  <button type="button" onClick={() => setBrowsing(true)}>
                    Mailers
                  </button>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>Mailers</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            {crumbsLeadBack && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{selectedLayout?.name ?? selectedMailer?.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <PageLayout.Header heading="Mailers" />
      <PageLayout.Body>
        {/* No height of its own: the page scrolls, so the form and the preview
            grow to their content and scroll together. */}
        <div className="tw:flex tw:flex-col tw:gap-4">
          {showsIndex ? (
            <MailerIndex
              groups={navGroups}
              layout={indexLayout}
              filterStyle={filterStyle}
              search={search}
              typeFilter={typeFilter}
              signposted={migration === 'signposted'}
              showOverrides={overridesUI !== 'off'}
              onManageOverrides={() => setOverridesIndexOpen(true)}
              overrideCount={(id) => liveOverrides(id).length}
              isEnabled={isEnabled}
              onToggleEnabled={toggleEnabled}
              onSearch={setSearch}
              onTypeFilter={setTypeFilter}
              onOpen={selectMailer}
            />
          ) : (
          <div className="tw:flex tw:min-h-0 tw:flex-1 tw:gap-6">
            {/* ------------------------------------------------------ workspace */}
            <div
              className={clsx(
                'tw:flex tw:min-w-0 tw:flex-1 tw:flex-col',
                'tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white'
              )}
            >
              <div className="tw:flex tw:flex-col">
                {/* Identity: who this mailer is, and the controls that act on it. */}
                <div className="tw:flex tw:flex-wrap tw:items-start tw:gap-4 tw:border-b tw:border-gray-cool-200 tw:px-6 tw:py-4">
                  <div className="tw:min-w-0 tw:flex-1">
                    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                      <h2 className="tw:text-lg tw:font-bold tw:text-gray-cool-900">
                        {selectedLayout?.name ?? selectedMailer?.name}
                      </h2>
                      {/* Zero while overrides are off, so the badge goes with them —
                          and counted live, so adding one is reflected here. */}
                      {selectedMailer && (
                        <StatusPills
                          mailer={selectedMailer}
                          enabled={draft.enabled}
                          overrideCount={overridesUI === 'off' ? 0 : liveOverrides(selectedMailer.id).length}
                        />
                      )}
                    </div>
                    <p className="tw:mt-0.5 tw:text-sm tw:text-gray-cool-600">
                      {selectedLayout ? `${selectedLayout.description} Used by ${selectedLayout.usedBy} mailers.` : selectedMailer?.description}
                    </p>
                  </div>

                </div>

                {/* The working bar: the controls that act on this mailer, the
                    way back to the catalogue, and the switch between editing it
                    and reading it. */}
                <div
                  className={clsx(
                    'tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:border-b tw:border-gray-cool-200 tw:px-6 tw:py-3'
                  )}
                >
                  {wayBack !== 'breadcrumb' && (
                    <Button rank="secondary" size="sm" leadingIcon="arrow-narrow-left" onClick={() => setBrowsing(true)}>
                      All mailers
                    </Button>
                  )}
                  {headerActions}
                  <div className="tw:ml-auto">{paneSwitcher}</div>
                </div>

                {/* Switched off is a state worth saying out loud, but not a
                    reason to lock the form: fixing the copy while it is off,
                    then switching it back on, is the ordinary way round. */}
                {selectedMailer?.suppressible && !draft.enabled && (
                  <div className="tw:flex tw:items-start tw:gap-3 tw:border-b tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:px-6 tw:py-3">
                    <Icon type="alert-circle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:text-gray-cool-500" />
                    <p className="tw:text-sm tw:text-gray-cool-700">
                      This mailer is switched off. Changes here are still saved — nothing is sent until it is switched back on.
                    </p>
                  </div>
                )}

                {/* migration notice */}
                {(locked || layoutsLocked) && (
                  <div
                    className={clsx(
                      'tw:flex tw:items-start tw:gap-3 tw:border-b tw:border-warning-200 tw:bg-warning-50 tw:px-6 tw:py-3'
                    )}
                  >
                    <Icon type="alert-triangle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:text-warning-600" />
                    <div className="tw:text-sm tw:text-warning-700">
                      <p className="tw:font-medium">
                        {layoutsLocked ? 'Layouts are not editable yet' : 'This mailer has not moved to the new system yet'}
                      </p>
                      <p className="tw:mt-0.5">
                        {layoutsLocked
                          ? 'This is a preview of what layout editing will look like once mailer layouts are templated.'
                          : 'Its content still lives in the old settings screen. Once it moves across, you will edit it here with smart fields and a live preview.'}
                      </p>
                      {selectedMailer?.legacyHref && (
                        <a
                          href={selectedMailer.legacyHref}
                          className="tw:mt-1 tw:inline-flex tw:items-center tw:gap-1 tw:font-medium tw:text-warning-800 tw:underline"
                        >
                          Edit it in the current settings
                          <Icon type="external-link" className="tw:h-3.5 tw:w-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* group-scope banner */}
                {activeGroup && (
                  <div
                    className={clsx(
                      'tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:border-b tw:border-sky-200 tw:bg-sky-50 tw:px-6 tw:py-3'
                    )}
                  >
                    <Icon type="users-01" className="tw:h-4 tw:w-4 tw:text-sky-600" />
                    <p className="tw:min-w-0 tw:flex-1 tw:text-sm tw:text-sky-800">
                      {perField ? (
                        <>
                          You are editing this mailer <strong>just for the {OWNER_KIND_SINGULAR[activeGroup.kind]} {activeGroup.name}</strong>
                          . Anything left as inherited keeps following the SupporterBase default, so later edits to the default still reach
                          it.
                        </>
                      ) : (
                        <>
                          You are editing a <strong>separate copy</strong> of this mailer for the {OWNER_KIND_SINGULAR[activeGroup.kind]}{' '}
                          <strong>{activeGroup.name}</strong>. It no longer follows the SupporterBase default, so later edits to the
                          default will not reach it — reset it if you want it back in step.
                        </>
                      )}
                    </p>
                    <Button rank="secondary" size="sm" color="danger" onClick={() => resetOverride(activeGroup.id)}>
                      Reset to the default
                    </Button>
                    <Button rank="secondary" size="sm" onClick={() => setScopeGroupId(null)}>
                      Back to the default
                    </Button>
                  </div>
                )}

                <div className="tw:flex tw:items-stretch">
                  {/* -------------------------------------------------- edit pane */}
                  {showEditPane && (
                    <div
                      className={clsx(
                        'tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-4 tw:px-6 tw:py-5'
                      )}
                    >
                      {!isLayout && (
                        <>
                          <div onFocus={() => setLastFocused('subject')}>
                            {activeGroup && perField && (
                              <FieldScopeNote
                                overridden={isOverridden('subject')}
                                ownerName={activeGroup.name}
                                disabled={locked}
                                onOverride={() => setFieldOverridden('subject', true)}
                                onRevert={() => setFieldOverridden('subject', false)}
                              />
                            )}
                            {smartFieldStyle === 'menu' && (
                              <div className="tw:mb-1 tw:flex tw:items-center tw:justify-between tw:gap-3">
                                <span className="tw:font-medium tw:text-gray-700">Subject</span>
                                <SmartFieldList
                                  groups={smartFields}
                                  variant="menu"
                                  fieldLabel="Subject"
                                  onInsert={(token) => insertInto('subject', token)}
                                />
                              </div>
                            )}
                            <TextInput
                              ref={subjectRef}
                              name="subject"
                              label="Subject"
                              hideLabel={smartFieldStyle === 'menu'}
                              disabled={locked || inherits('subject')}
                              value={draft.subject}
                              onChange={(event) => update({ subject: event.target.value })}
                            />
                          </div>

                          <div onFocus={() => setLastFocused('preheader')}>
                            {activeGroup && perField && (
                              <FieldScopeNote
                                overridden={isOverridden('preheader')}
                                ownerName={activeGroup.name}
                                disabled={locked}
                                onOverride={() => setFieldOverridden('preheader', true)}
                                onRevert={() => setFieldOverridden('preheader', false)}
                              />
                            )}
                            {smartFieldStyle === 'menu' && (
                              <div className="tw:mb-1 tw:flex tw:items-center tw:justify-between tw:gap-3">
                                <span className="tw:font-medium tw:text-gray-700">Preheader</span>
                                <SmartFieldList
                                  groups={smartFields}
                                  variant="menu"
                                  fieldLabel="Preheader"
                                  onInsert={(token) => insertInto('preheader', token)}
                                />
                              </div>
                            )}
                            <TextInput
                              ref={preheaderRef}
                              name="preheader"
                              label="Preheader"
                              hideLabel={smartFieldStyle === 'menu'}
                              description="The grey line of text inboxes show after the subject."
                              disabled={locked || inherits('preheader')}
                              value={draft.preheader}
                              onChange={(event) => update({ preheader: event.target.value })}
                            />
                          </div>

                          {/* `from_name` and `reply_to_email` are already columns on
                              mailer_templates, so an owner's row carries its own —
                              per-override sender identity comes for free. */}
                          <div className="tw:flex tw:flex-wrap tw:gap-4">
                            <div className="tw:min-w-48 tw:flex-1">
                              {activeGroup && perField && (
                                <FieldScopeNote
                                  overridden={isOverridden('fromName')}
                                  ownerName={activeGroup.name}
                                  disabled={locked}
                                  onOverride={() => setFieldOverridden('fromName', true)}
                                  onRevert={() => setFieldOverridden('fromName', false)}
                                />
                              )}
                              <TextInput
                                name="fromName"
                                label="From name"
                                description={
                                  activeGroup
                                    ? `Shown as the sender for ${activeGroup.name}. Blank uses the SupporterBase default.`
                                    : 'Leave blank to use the SupporterBase default.'
                                }
                                disabled={locked || inherits('fromName')}
                                value={draft.fromName}
                                onChange={(event) => update({ fromName: event.target.value })}
                              />
                            </div>
                            <div className="tw:min-w-48 tw:flex-1">
                              {activeGroup && perField && (
                                <FieldScopeNote
                                  overridden={isOverridden('replyTo')}
                                  ownerName={activeGroup.name}
                                  disabled={locked}
                                  onOverride={() => setFieldOverridden('replyTo', true)}
                                  onRevert={() => setFieldOverridden('replyTo', false)}
                                />
                              )}
                              <TextInput
                                name="replyTo"
                                label="Reply-to address"
                                description={
                                  activeGroup
                                    ? `Where replies from ${activeGroup.name} recipients land. Blank uses the SupporterBase default.`
                                    : 'Leave blank to use the SupporterBase default.'
                                }
                                disabled={locked || inherits('replyTo')}
                                value={draft.replyTo}
                                onChange={(event) => update({ replyTo: event.target.value })}
                              />
                            </div>
                          </div>

                          {/* Its own row: which chrome wraps the email is a different
                              decision from who it comes from, and a react-select control
                              does not line up with the text inputs beside it anyway.

                              Unlike the sender fields, layout is not per-owner because
                              there is no layout column — or layout model — yet. That is
                              the follow-up work, not a deliberate restriction. */}
                          <div className="tw:max-w-sm">
                            <SelectField
                              name="layout"
                              label="Layout"
                              disabled={locked || activeGroup !== null}
                              value={draft.layoutId}
                              onValueChange={(layoutId) => update({ layoutId })}
                              options={LAYOUTS.map((layout) => ({ value: layout.id, label: layout.name }))}
                            />
                            {activeGroup && (
                              <p className="tw:pt-1 tw:text-sm tw:text-gray-cool-600">
                                Set for the whole SupporterBase for now — layouts are not editable yet.
                              </p>
                            )}
                          </div>
                        </>
                      )}

                      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                        <span className="tw:font-medium tw:text-gray-700">{isLayout ? 'Layout HTML' : 'Body'}</span>
                        {smartFieldStyle === 'menu' && (
                          <SmartFieldList
                            groups={smartFields}
                            variant="menu"
                            fieldLabel={isLayout ? 'Layout HTML' : 'Body'}
                            onInsert={(token) => insertInto('body', token)}
                          />
                        )}
                      </div>


                      {activeGroup && perField && (
                        <FieldScopeNote
                          overridden={isOverridden('bodyHtml')}
                          ownerName={activeGroup.name}
                          disabled={locked}
                          onOverride={() => setFieldOverridden('bodyHtml', true)}
                          onRevert={() => setFieldOverridden('bodyHtml', false)}
                        />
                      )}

                      <div onFocus={() => setLastFocused('body')}>
                        <BodyEditor
                          editorKey={overrideKey}
                          value={draft.bodyHtml}
                          disabled={locked || layoutsLocked || inherits('bodyHtml')}
                          onChange={(content) => update({ bodyHtml: content })}
                        />
                      </div>

                    </div>
                  )}

                  {panelBeside && (
                    <aside
                      aria-label="Smart fields"
                      className="tw:w-72 tw:shrink-0 tw:self-start tw:border-l tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:p-4 tw:lg:sticky tw:lg:top-4"
                    >
                      {smartFieldsPanel}
                    </aside>
                  )}

                  {/* ----------------------------------------------- preview pane */}
                  {/* The divider between the two columns is drawn by this one, row
                      by row, rather than as a border down the side of the form —
                      so it stops with the preview instead of ruling a line beside
                      the space left when the email is shorter than the form. */}
                  {showPreviewPane && (
                    <div
                      className={clsx(
                        'tw:flex tw:min-w-0 tw:flex-col',
                        roomForBoth ? 'tw:w-[45%]' : 'tw:flex-1'
                      )}
                    >
                      <div
                        className={clsx(
                          'tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:border-b tw:border-gray-cool-200 tw:px-4 tw:py-2.5',
                          roomForBoth && 'tw:border-l'
                        )}
                      >
                        <span className="tw:text-sm tw:font-bold tw:text-gray-cool-700">Preview</span>
                        <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">
                          <ViewToggle
                            label="Preview format"
                            size="sm"
                            value={previewFormat}
                            onChange={(value) => setPreviewFormat(value as 'html' | 'text')}
                            options={[
                              { value: 'html', label: 'HTML' },
                              { value: 'text', label: 'Plain text' },
                            ]}
                          />
                          <ViewToggle
                            label="Preview device"
                            size="sm"
                            value={previewDevice}
                            onChange={(value) => setPreviewDevice(value as 'desktop' | 'mobile')}
                            options={[
                              { value: 'desktop', label: 'Desktop' },
                              { value: 'mobile', label: 'Mobile' },
                            ]}
                          />
                        </div>
                      </div>

                      {/* The envelope, and the two controls that belong to what is
                          in it rather than to how it renders: which data fills it,
                          and seeing it at full size. */}
                      {!isLayout && (
                        <div
                          className={clsx(
                            'tw:flex tw:flex-wrap tw:items-start tw:gap-3 tw:border-b tw:border-gray-cool-200 tw:bg-white tw:px-4 tw:py-3',
                            roomForBoth && 'tw:border-l'
                          )}
                        >
                          <div className="tw:min-w-0 tw:flex-1">
                            <p className="tw:text-sm tw:font-bold tw:text-gray-cool-900">
                              {renderedSubject || <span className="tw:text-gray-cool-400">No subject</span>}
                            </p>
                            <p className="tw:text-xs tw:text-gray-cool-500">
                              {draft.fromName || sample.instanceName} · {renderedPreheader}
                            </p>
                          </div>
                          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
                            <Button rank="secondary" size="sm" leadingIcon="settings-01" onClick={() => setSampleOpen(true)}>
                              Sample data
                            </Button>
                            <Button rank="secondary" size="sm" leadingIcon="external-link" onClick={openPreviewInNewTab}>
                              Open
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className={clsx(roomForBoth && 'tw:border-l tw:border-gray-cool-200')}>
                        {previewFormat === 'html' ? (
                          <EmailPreview html={composed} width={previewDevice === 'mobile' ? 390 : null} />
                        ) : (
                          <pre className="tw:whitespace-pre-wrap tw:bg-gray-cool-50 tw:p-4 tw:font-mono tw:text-xs tw:text-gray-cool-700">
                            {toPlainText(renderedBody)}
                          </pre>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* The one thing that does not scroll away: whether there is
                  anything to save, and the buttons that do it. */}
              <div className="tw:sticky tw:bottom-0 tw:z-20 tw:flex tw:items-center tw:gap-3 tw:rounded-b-xl tw:border-t tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:px-6 tw:py-3">
                {saveBar}
              </div>
            </div>
          </div>
          )}
        </div>

        {overridesUI !== 'off' && (
          <OverridesIndexSheet
            open={overridesIndexOpen}
            onOpenChange={setOverridesIndexOpen}
            entries={overrideEntries}
            onAdd={setPickerMailerId}
          />
        )}

        {pickerMailer?.overrideScope && (
          <OwnerPickerSheet
            open={pickerMailerId !== null}
            onOpenChange={(open) => !open && setPickerMailerId(null)}
            owners={OVERRIDE_OWNERS.filter((owner) => owner.kind === pickerMailer.overrideScope)}
            overriddenIds={liveOverrides(pickerMailer.id).map((entry) => entry.groupId)}
            kind={pickerMailer.overrideScope}
            onAdd={(ownerIds) => {
              addOverridesFor(pickerMailer.id, ownerIds)
              setPickerMailerId(null)
            }}
          />
        )}

        {/* -------------------------------------------------- sample data sheet */}
        <Sheet open={sampleOpen} onOpenChange={setSampleOpen}>
          <SheetContent className="tw:w-104">
            <SheetTitle>Preview sample data</SheetTitle>
            <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
              <p className="tw:text-sm tw:text-gray-cool-600">
                These values stand in for the real supporter, event and shift data when the mailer is rendered. They only affect the
                preview.
              </p>

              <TextInput
                name="sampleRecipient"
                label="Recipient first name"
                value={sample.recipientFirstName}
                onChange={(event) => setSample({ ...sample, recipientFirstName: event.target.value })}
              />
              <TextInput
                name="sampleEvent"
                label="Event name"
                value={sample.eventName}
                onChange={(event) => setSample({ ...sample, eventName: event.target.value })}
              />
              <TextInput
                name="sampleLocation"
                label="Event location"
                description="Clear this to see how the mailer looks when an event has no venue."
                value={sample.eventLocation}
                onChange={(event) => setSample({ ...sample, eventLocation: event.target.value })}
              />

              <SegControl
                label="Number of shifts"
                value={String(sample.shiftCount)}
                options={[
                  { value: '1', label: '1' },
                  { value: '2', label: '2' },
                  { value: '3', label: '3' },
                ]}
                onChange={(value) => setSample({ ...sample, shiftCount: Number(value) })}
              />

              <SegControl
                label="Other upcoming shifts"
                value={String(sample.otherRsvpCount)}
                options={[
                  { value: '0', label: 'None' },
                  { value: '1', label: '1 event' },
                  { value: '2', label: '2 events' },
                ]}
                onChange={(value) => setSample({ ...sample, otherRsvpCount: Number(value) })}
              />

              <TextInput
                name="samplePerson"
                label="Supporter / user name"
                value={sample.personName}
                onChange={(event) => setSample({ ...sample, personName: event.target.value })}
              />
              <TextInput
                name="sampleGroup"
                label="Group name"
                value={sample.groupName}
                onChange={(event) => setSample({ ...sample, groupName: event.target.value })}
              />

              <div className="tw:flex tw:gap-2 tw:pt-2">
                <Button rank="secondary" size="sm" onClick={() => setSample(DEFAULT_SAMPLE)}>
                  Reset to defaults
                </Button>
                <Button size="sm" onClick={() => setSampleOpen(false)}>
                  Done
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Design tools: dev-only floating overlay, portaled to body, default closed */}
        {createPortal(
          <>
            <button
              onClick={() => setShowDesign((state) => !state)}
              // Clears the save bar at the foot of the workspace.
              className="tw:fixed tw:bottom-20 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
            >
              <Icon type="eye" className="tw:h-4 tw:w-4" />
              {showDesign ? 'Hide design tools' : 'Design tools'}
            </button>
            {showDesign && (
              <div
                className={clsx(
                  // Enough toggles now that the panel can outgrow the viewport.
                  'tw:fixed tw:bottom-32 tw:left-4 tw:z-50 tw:w-[min(720px,calc(100vw-2rem))] tw:max-h-[calc(100dvh-12rem)] tw:overflow-y-auto tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl'
                )}
              >
                <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                  <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" />
                  <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                  <button
                    onClick={() => setShowDesign(false)}
                    aria-label="Hide design tools"
                    className="tw:ml-auto tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600"
                  >
                    <Icon type="x-close" className="tw:h-4 tw:w-4" />
                  </button>
                </div>
                <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                  This panel is purely for exploring the design and is not part of the product.
                  <br />
                  Questo pannello serve solo a esplorare il design e non fa parte del prodotto.
                </p>
                <div className="tw:flex tw:flex-wrap tw:gap-8">
                  <SegControl
                    label="Way back"
                    value={wayBack}
                    onChange={(value) => setWayBack(value as 'breadcrumb' | 'button' | 'both')}
                    options={[
                      { value: 'breadcrumb', label: 'Breadcrumb' },
                      { value: 'button', label: 'Button' },
                      { value: 'both', label: 'Both' },
                    ]}
                  />
                  <SegControl
                    label="Type filter"
                    value={filterStyle}
                    onChange={(value) => setFilterStyle(value as FilterStyle)}
                    options={[
                      { value: 'dropdown', label: 'Dropdown' },
                      { value: 'pills', label: 'Pills' },
                    ]}
                  />
                  <SegControl
                    label="Index layout"
                    value={indexLayout}
                    onChange={(value) => {
                      setIndexLayout(value as IndexLayout)
                      setBrowsing(true)
                    }}
                    options={[
                      { value: 'table', label: 'Table' },
                      { value: 'list', label: 'List' },
                      { value: 'cards', label: 'Cards' },
                    ]}
                  />
                  <SegControl
                    label="Smart fields"
                    value={smartFieldStyle}
                    onChange={(value) => setSmartFieldStyle(value as 'panel' | 'menu')}
                    options={[
                      { value: 'panel', label: 'Panel display' },
                      { value: 'menu', label: 'Toolbar menu' },
                    ]}
                  />
                  <SegControl
                    label="Overrides"
                    value={overridesUI}
                    onChange={(value) => {
                      setOverridesUI(value as 'off' | 'index')
                      setScopeGroupId(null)
                    }}
                    options={[
                      { value: 'off', label: 'Instance-wide only' },
                      { value: 'index', label: 'Overrides index' },
                    ]}
                  />
                  <SegControl
                    label="Override granularity"
                    value={overrideGranularity}
                    onChange={(value) => setOverrideGranularity(value as 'row' | 'field')}
                    options={[
                      { value: 'row', label: 'Whole copy' },
                      { value: 'field', label: 'Per field' },
                    ]}
                  />
                  <SegControl
                    label="Migration stage"
                    value={migration}
                    onChange={(value) => {
                      const stage = value as MigrationStage
                      setMigration(stage)
                      if (stage === 'shipping' && !MAILERS.some((mailer) => mailer.id === selectedId && mailer.migrated)) {
                        selectMailer(MAILERS.find((mailer) => mailer.migrated)?.id ?? selectedId)
                      }
                    }}
                    options={[
                      { value: 'shipping', label: 'Migrated only' },
                      { value: 'signposted', label: 'Signposted' },
                      { value: 'complete', label: 'All migrated' },
                    ]}
                  />
                </div>
              </div>
            )}
          </>,
          document.body
        )}
      </PageLayout.Body>

    </PageLayout.Root>
  )
}

export default MailersSettingsPreview
